// src/components/LegalPanel.js — Phase-1 UI for the legal-document agents (August-style).
// Two modes: "Ask a contract" (cited answers) and "Compare contracts" (clause table).
// Drives /api/legal via legal-api.js. See AUGUST_CLONE_PLAN.md.
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { askDocument, tabularReview, listTabularTemplates, createTabularTemplate, diffVersions, runDiligence, draftClause, assistantRequest, streamAgentRun, generateMemo, extractKeyTerms, extractObligations, obligationsICS, listPlaybooks, playbookReview, listMyPlaybooks, createMyPlaybook, updateMyPlaybook, deleteMyPlaybook, listFolders, createFolder, deleteFolder, listFolderDocuments, addFolderDocument, deleteFolderDocument, listWorkflows, createWorkflow, deleteWorkflow, listActivity, logActivity, clearActivity, dmsStatus, dmsConnect, dmsDisconnect, dmsList, dmsImport, listMatters, createMatter, updateMatter, deleteMatter, runMatterDiligence, buildMatterCalendar, matterCalendarICS, matterMemo, getFirmProfile, saveFirmProfile, listApiKeys, createApiKey, deleteApiKey, fileToBase64 } from '../utils/legal-api';
import { auth } from '../firebase/firebaseConfig';

const BORDER = '1px solid #e2e0d6';
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const card = { background: '#fff', border: BORDER, borderRadius: 10, padding: 18, marginBottom: 16 };
const label = { display: 'block', fontSize: 13, fontWeight: 600, color: '#3a3a32', marginBottom: 6 };
const input = { width: '100%', padding: '9px 11px', border: BORDER, borderRadius: 7, fontSize: 14, fontFamily: FONT, boxSizing: 'border-box' };
const btn = (disabled) => ({
  padding: '9px 18px', borderRadius: 7, border: 'none', fontSize: 14, fontWeight: 600,
  background: disabled ? '#c9c7bd' : '#1d1d1b', color: '#fff', cursor: disabled ? 'default' : 'pointer',
});

function citationLabel(c) {
  if (!c) return '';
  if (c.type === 'page_location') return `p.${c.start_page_number}${c.end_page_number - 1 > c.start_page_number ? `–${c.end_page_number - 1}` : ''}`;
  if (c.type === 'char_location') return `chars ${c.start_char_index}–${c.end_char_index}`;
  if (c.type === 'content_block_location') return `block ${c.start_block_index}`;
  return 'source';
}

// --- Memo deliverable: Markdown → formatted document, exportable to Word/PDF -----
// Minimal, SAFE Markdown→HTML: HTML-escape first (memo text is model-generated; never
// trusted as markup), then apply headings / bold / italics / bullet lists / paragraphs.
function memoToHtml(md) {
  const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const inline = (s) => esc(s)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*\n]+?)\*/g, '$1<em>$2</em>');
  const out = [];
  let inList = false;
  const closeList = () => { if (inList) { out.push('</ul>'); inList = false; } };
  for (const raw of String(md || '').split(/\r?\n/)) {
    const line = raw.trimEnd();
    if (/^#{1,6}\s/.test(line)) {
      closeList();
      const level = Math.min(line.match(/^#+/)[0].length, 4);
      out.push(`<h${level}>${inline(line.replace(/^#+\s/, ''))}</h${level}>`);
    } else if (/^\s*[-*]\s+/.test(line)) {
      if (!inList) { out.push('<ul>'); inList = true; }
      out.push(`<li>${inline(line.replace(/^\s*[-*]\s+/, ''))}</li>`);
    } else if (!line.trim()) {
      closeList();
    } else {
      closeList();
      out.push(`<p>${inline(line)}</p>`);
    }
  }
  closeList();
  return out.join('\n');
}

function memoHtmlDocument(title, md) {
  const body = memoToHtml(md);
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${String(title).replace(/[<>]/g, '')}</title>`
    + `<style>body{font-family:Georgia,'Times New Roman',serif;font-size:12pt;line-height:1.5;color:#111;max-width:720px;margin:48px auto;padding:0 16px;}`
    + `h1{font-size:18pt;margin:0 0 12px}h2{font-size:14pt;margin:20px 0 6px}h3,h4{font-size:12.5pt;margin:16px 0 4px}`
    + `p{margin:0 0 10px}ul{margin:0 0 10px 22px}li{margin:3px 0}</style></head><body>${body}</body></html>`;
}

function slugify(s) { return String(s || 'memo').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '').slice(0, 50) || 'memo'; }

// Word export: an HTML document with the msword MIME opens natively in Microsoft Word
// (dependency-free; true .docx would need the `docx` library — a later upgrade).
function downloadWord(title, md) {
  const blob = new Blob(['﻿', memoHtmlDocument(title, md)], { type: 'application/msword' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${slugify(title)}.doc`; a.click();
  URL.revokeObjectURL(url);
}

// PDF export via the browser's print-to-PDF on a clean print window.
function printPdf(title, md) {
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(memoHtmlDocument(title, md));
  w.document.close(); w.focus();
  setTimeout(() => { try { w.print(); } catch { /* noop */ } }, 350);
}

// Render a memo as a formatted on-screen document (escaped HTML → safe innerHTML).
function MemoDocument({ md }) {
  return (
    <div
      style={{ fontFamily: 'Georgia, serif', fontSize: 14, lineHeight: 1.65, color: '#26261f' }}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: memoToHtml(md) }}
    />
  );
}

const exportBtn = { background: 'none', border: BORDER, borderRadius: 6, padding: '4px 10px', fontSize: 12.5, color: '#1d1d1b', cursor: 'pointer' };

// --- "Ask a contract" tab -------------------------------------------------
function AskTab() {
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [pdfUrl, setPdfUrl] = useState('');   // object URL for side-by-side viewer
  const [pdfPage, setPdfPage] = useState(1);

  // Build/tear down an object URL for the uploaded PDF so we can show it side-by-side.
  React.useEffect(() => {
    if (file && (file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf'))) {
      const url = URL.createObjectURL(file);
      setPdfUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPdfUrl('');
    return undefined;
  }, [file]);

  const citePage = (c) => (c && c.type === 'page_location' ? c.start_page_number : null);
  const jumpTo = (c) => { const p = citePage(c); if (p) setPdfPage(p); };

  const run = async () => {
    setError(''); setResult(null); setLoading(true);
    try {
      const payload = { documentTitle: title || (file ? file.name : 'Document'), question };
      if (file) {
        payload.documentBase64 = await fileToBase64(file);
        payload.mediaType = file.type || 'application/pdf';
      } else {
        payload.documentText = text;
      }
      const res = await askDocument(payload);
      setResult(res);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Number the cited segments so we can show a sources list underneath.
  const footnotes = [];
  result?.segments?.forEach((seg) => {
    (seg.citations || []).forEach((c) => footnotes.push(c));
  });

  return (
    <div>
      <div style={card}>
        <label style={label}>Contract</label>
        <input style={{ ...input, marginBottom: 10 }} placeholder="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} />
        <input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files[0] || null)} style={{ marginBottom: 10, display: 'block', fontSize: 13 }} />
        {!file && (
          <textarea style={{ ...input, minHeight: 120, fontFamily: 'inherit' }} placeholder="…or paste the contract text here" value={text} onChange={(e) => setText(e.target.value)} />
        )}
        {file && <div style={{ fontSize: 13, color: '#6b6a5f' }}>📄 {file.name} — <button onClick={() => setFile(null)} style={{ background: 'none', border: 'none', color: '#1d1d1b', cursor: 'pointer', padding: 0 }}>remove</button></div>}
        <label style={{ ...label, marginTop: 14 }}>Question</label>
        <input style={input} placeholder="e.g. What is the liability cap and governing law?" value={question} onChange={(e) => setQuestion(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && !loading && run()} />
        <div style={{ marginTop: 14 }}>
          <button style={btn(loading || !question || (!text && !file))} disabled={loading || !question || (!text && !file)} onClick={run}>
            {loading ? 'Analyzing…' : 'Ask'}
          </button>
        </div>
        {error && <div style={{ color: '#b3261e', fontSize: 13, marginTop: 10 }}>{error}</div>}
      </div>

      {result && (
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ ...card, flex: '1 1 340px', minWidth: 300 }}>
            <div style={{ fontSize: 15, lineHeight: 1.7, color: '#26261f' }}>
              {result.segments.map((seg, i) => {
                const cited = (seg.citations || []).length > 0;
                const c = cited ? seg.citations[0] : null;
                return (
                  <span key={i} style={cited ? { background: '#fbf1d9', borderBottom: '1px solid #e6c86a' } : {}}>
                    {seg.text}
                    {cited && (
                      <sup
                        onClick={() => jumpTo(c)}
                        title={citePage(c) ? `Jump to page ${citePage(c)}` : undefined}
                        style={{ color: '#1d1d1b', fontWeight: 700, fontSize: 11, cursor: citePage(c) ? 'pointer' : 'default' }}
                      >[{citationLabel(c)}]</sup>
                    )}
                  </span>
                );
              })}
            </div>
            {footnotes.length > 0 && (
              <div style={{ marginTop: 16, borderTop: BORDER, paddingTop: 12 }}>
                <div style={{ ...label, marginBottom: 8 }}>Sources</div>
                {footnotes.map((c, i) => (
                  <div key={i} onClick={() => jumpTo(c)} style={{ fontSize: 12.5, color: '#5a5950', marginBottom: 6, cursor: citePage(c) ? 'pointer' : 'default' }}>
                    <strong style={{ color: '#1d1d1b' }}>{citationLabel(c)}</strong> “{c.cited_text}”
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Side-by-side source PDF (August's citation-anchored viewer) */}
          {pdfUrl && (
            <div style={{ ...card, flex: '1 1 360px', minWidth: 300, padding: 8 }}>
              <div style={{ fontSize: 12, color: '#8a897e', padding: '4px 6px 8px' }}>📄 Source · click a [p.N] citation to jump · showing p.{pdfPage}</div>
              <iframe
                title="Source document"
                src={`${pdfUrl}#page=${pdfPage}&view=FitH`}
                style={{ width: '100%', height: 620, border: BORDER, borderRadius: 6 }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Built-in column templates (August ships named comparisons like Debt-covenant).
const TABULAR_PRESETS = {
  'debt-covenant': {
    label: 'Debt-covenant comparison',
    columns: [
      { name: 'Borrower / Facility', type: 'tag' }, { name: 'Financial covenants', type: 'text' },
      { name: 'Leverage ratio', type: 'text' }, { name: 'Interest coverage ratio', type: 'text' },
      { name: 'Negative covenants', type: 'text' }, { name: 'Events of default', type: 'text' },
      { name: 'Change of control', type: 'text' }, { name: 'Reporting requirements', type: 'text' },
    ],
  },
  'nda': {
    label: 'NDA comparison',
    columns: [
      { name: 'Parties', type: 'text' }, { name: 'Mutual or one-way', type: 'tag' },
      { name: 'Definition of confidential info', type: 'text' }, { name: 'Term of confidentiality', type: 'text' },
      { name: 'Permitted use', type: 'text' }, { name: 'Return / destruction', type: 'text' }, { name: 'Governing law', type: 'tag' },
    ],
  },
  'msa': {
    label: 'MSA / services comparison',
    columns: [
      { name: 'Parties', type: 'text' }, { name: 'Term', type: 'text' }, { name: 'Liability cap', type: 'text' },
      { name: 'Indemnity', type: 'text' }, { name: 'IP ownership', type: 'text' }, { name: 'Termination', type: 'text' },
      { name: 'Data protection', type: 'text' }, { name: 'Governing law', type: 'tag' },
    ],
  },
};

// --- "Tabular Review" tab — sourced, editable comparison grid -------------
function CompareTab() {
  const [docs, setDocs] = useState([{ title: '', text: '', file: null }, { title: '', text: '', file: null }]);
  const [columns, setColumns] = useState([]); // [{ name, type }]
  const [colDraft, setColDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [templates, setTemplates] = useState([]);

  const reloadTemplates = () => listTabularTemplates().then((d) => setTemplates(d.templates || [])).catch(() => {});
  React.useEffect(() => { reloadTemplates(); }, []);

  const applyTemplate = (val) => {
    if (val.startsWith('preset:')) { const p = TABULAR_PRESETS[val.slice(7)]; if (p) setColumns(p.columns.map((c) => ({ ...c }))); }
    else if (val.startsWith('saved:')) { const t = templates.find((x) => x.id === val.slice(6)); if (t) setColumns(t.columns.map((c) => ({ name: c.name, type: c.type }))); }
  };
  const saveTemplate = async () => {
    if (!columns.length) return;
    // eslint-disable-next-line no-alert
    const name = window.prompt('Template name:');
    if (!name || !name.trim()) return;
    try { await createTabularTemplate({ name: name.trim(), columns }); await reloadTemplates(); } catch (e) { setError(e.message); }
  };

  const setDoc = (i, patch) => setDocs((ds) => ds.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  const addDoc = () => setDocs((ds) => (ds.length >= 8 ? ds : [...ds, { title: '', text: '', file: null }]));
  const removeDoc = (i) => setDocs((ds) => ds.filter((_, j) => j !== i));
  const addColumn = () => {
    const name = colDraft.trim();
    if (!name || columns.find((c) => c.name.toLowerCase() === name.toLowerCase())) { setColDraft(''); return; }
    setColumns((cs) => [...cs, { name, type: 'text' }]);
    setColDraft('');
  };

  const run = async () => {
    setError(''); setResult(null); setLoading(true);
    try {
      const documents = [];
      for (let i = 0; i < docs.length; i++) {
        const d = docs[i];
        if (!d.file && !String(d.text).trim()) continue;
        const entry = { title: d.title || (d.file ? d.file.name : `Document ${i + 1}`) };
        if (d.file) { entry.base64 = await fileToBase64(d.file); entry.mediaType = d.file.type || 'application/pdf'; }
        else entry.text = d.text;
        documents.push(entry);
      }
      if (documents.length === 0) throw new Error('Add at least one document');
      setResult(await tabularReview({ documents, columns }));
      logActivity({ kind: 'tabular', title: `Tabular review · ${documents.length} doc${documents.length === 1 ? '' : 's'}`, summary: columns.map((c) => c.name).join(', ') });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 8 }}>
          <label style={{ ...label, marginBottom: 0 }}>Columns to extract (optional)</label>
          <select value="" onChange={(e) => { applyTemplate(e.target.value); e.target.value = ''; }} style={{ fontSize: 12.5, color: '#54534a', border: BORDER, borderRadius: 8, padding: '5px 8px', background: '#faf9f4', cursor: 'pointer', fontFamily: FONT }}>
            <option value="" disabled>Use a template…</option>
            <optgroup label="Built-in">
              {Object.entries(TABULAR_PRESETS).map(([k, p]) => <option key={k} value={`preset:${k}`}>{p.label}</option>)}
            </optgroup>
            {templates.length > 0 && (
              <optgroup label="My templates">
                {templates.map((t) => <option key={t.id} value={`saved:${t.id}`}>{t.name} ({t.columnCount})</option>)}
              </optgroup>
            )}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input style={input} placeholder="e.g. Liability cap" value={colDraft} onChange={(e) => setColDraft(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addColumn())} />
          <button onClick={addColumn} style={{ ...btn(false), background: '#fff', color: '#1d1d1b', border: BORDER, whiteSpace: 'nowrap' }}>+ Column</button>
          {columns.length > 0 && <button onClick={saveTemplate} style={{ ...btn(false), background: '#fff', color: '#1d1d1b', border: BORDER, whiteSpace: 'nowrap' }} title="Save these columns as a reusable template">🖫 Save</button>}
        </div>
        {columns.length > 0 ? (
          <div style={{ marginTop: 10 }}>
            {columns.map((c, i) => (
              <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#3a3a32', background: '#f3f1e9', borderRadius: 6, padding: '4px 8px', marginRight: 6, marginBottom: 6 }}>
                {c.name}
                <button onClick={() => setColumns((cs) => cs.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: '#1d1d1b', cursor: 'pointer', padding: 0 }}>×</button>
              </span>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 12.5, color: '#8a897e', marginTop: 8 }}>Leave blank to let the agent pick the salient columns (parties, term, liability cap, termination…).</div>
        )}
      </div>

      {docs.map((d, i) => (
        <div key={i} style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <label style={{ ...label, marginBottom: 0 }}>Document {i + 1}</label>
            {docs.length > 1 && <button onClick={() => removeDoc(i)} style={{ background: 'none', border: 'none', color: '#1d1d1b', cursor: 'pointer', fontSize: 13 }}>remove</button>}
          </div>
          <input style={{ ...input, marginBottom: 10 }} placeholder="Title (optional)" value={d.title} onChange={(e) => setDoc(i, { title: e.target.value })} />
          <input type="file" accept="application/pdf" onChange={(e) => setDoc(i, { file: e.target.files[0] || null })} style={{ marginBottom: 10, display: 'block', fontSize: 13 }} />
          {!d.file && <textarea style={{ ...input, minHeight: 80, fontFamily: 'inherit' }} placeholder="…or paste text" value={d.text} onChange={(e) => setDoc(i, { text: e.target.value })} />}
          {d.file && <div style={{ fontSize: 13, color: '#6b6a5f' }}>📄 {d.file.name}</div>}
        </div>
      ))}

      <div style={{ marginBottom: 16 }}>
        {docs.length < 8 && <button onClick={addDoc} style={{ ...btn(false), background: '#fff', color: '#1d1d1b', border: BORDER, marginRight: 10 }}>+ Add document</button>}
        <button style={btn(loading)} disabled={loading} onClick={run}>{loading ? 'Building grid…' : 'Build review grid'}</button>
      </div>
      {error && <div style={{ color: '#b3261e', fontSize: 13, marginBottom: 12 }}>{error}</div>}

      {result && <CompareResult result={result} />}
    </div>
  );
}

// --- "Due diligence" tab --------------------------------------------------
const SEV_COLOR = { high: '#b3261e', medium: '#b07d2b', low: '#8a897e' };
const STATUS_COLOR = { open: '#b07d2b', satisfied: '#3f7d4f', flag: '#b3261e' };
const VERDICT = {
  supported: { label: '✓ verified', color: '#3f7d4f' },
  unsupported: { label: '✗ unverified', color: '#b3261e' },
  uncertain: { label: '? uncertain', color: '#a07a1e' },
  error: { label: '— check failed', color: '#8a897e' },
};

function DiligenceTab() {
  const [docs, setDocs] = useState([{ title: '', text: '', file: null }]);
  const [dealContext, setDealContext] = useState('');
  const [verify, setVerify] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const setDoc = (i, patch) => setDocs((ds) => ds.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  const addDoc = () => setDocs((ds) => (ds.length >= 12 ? ds : [...ds, { title: '', text: '', file: null }]));
  const removeDoc = (i) => setDocs((ds) => ds.filter((_, j) => j !== i));

  const run = async () => {
    setError(''); setResult(null); setLoading(true);
    try {
      const documents = [];
      for (let i = 0; i < docs.length; i++) {
        const d = docs[i];
        if (!d.file && !String(d.text).trim()) continue;
        const entry = { title: d.title || (d.file ? d.file.name : `Document ${i + 1}`) };
        if (d.file) { entry.base64 = await fileToBase64(d.file); entry.mediaType = d.file.type || 'application/pdf'; }
        else entry.text = d.text;
        documents.push(entry);
      }
      if (documents.length === 0) throw new Error('Add at least one document');
      const r = await runDiligence({ documents, dealContext, verify });
      setResult(r);
      logActivity({ kind: 'diligence', title: `Diligence · ${documents.length} doc${documents.length === 1 ? '' : 's'}`, summary: `${r.findings?.length || 0} findings` });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={card}>
        <label style={label}>Deal context (optional)</label>
        <textarea style={{ ...input, minHeight: 60, fontFamily: 'inherit' }} placeholder="e.g. Acquisition of a SaaS target; buyer's counsel; focus on IP and change-of-control." value={dealContext} onChange={(e) => setDealContext(e.target.value)} />
        <label style={{ fontSize: 13, color: '#3a3a32', marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={verify} onChange={(e) => setVerify(e.target.checked)} />
          Adversarially verify each serious finding against its source
        </label>
      </div>

      {docs.map((d, i) => (
        <div key={i} style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <label style={{ ...label, marginBottom: 0 }}>Document {i + 1}</label>
            {docs.length > 1 && <button onClick={() => removeDoc(i)} style={{ background: 'none', border: 'none', color: '#1d1d1b', cursor: 'pointer', fontSize: 13 }}>remove</button>}
          </div>
          <input style={{ ...input, marginBottom: 10 }} placeholder="Title (optional)" value={d.title} onChange={(e) => setDoc(i, { title: e.target.value })} />
          <input type="file" accept="application/pdf" onChange={(e) => setDoc(i, { file: e.target.files[0] || null })} style={{ marginBottom: 10, display: 'block', fontSize: 13 }} />
          {!d.file && <textarea style={{ ...input, minHeight: 80, fontFamily: 'inherit' }} placeholder="…or paste text" value={d.text} onChange={(e) => setDoc(i, { text: e.target.value })} />}
          {d.file && <div style={{ fontSize: 13, color: '#6b6a5f' }}>📄 {d.file.name}</div>}
        </div>
      ))}

      <div style={{ marginBottom: 16 }}>
        {docs.length < 12 && <button onClick={addDoc} style={{ ...btn(false), background: '#fff', color: '#1d1d1b', border: BORDER, marginRight: 10 }}>+ Add document</button>}
        <button style={btn(loading)} disabled={loading} onClick={run}>{loading ? 'Reviewing…' : 'Run diligence'}</button>
      </div>
      {error && <div style={{ color: '#b3261e', fontSize: 13, marginBottom: 12 }}>{error}</div>}

      {result && (
        <div>
          {result.summary && <div style={{ ...card, fontSize: 14.5, lineHeight: 1.6, color: '#26261f' }}>{result.summary}</div>}

          {result.findings?.length > 0 && (
            <div style={card}>
              <div style={{ ...label, marginBottom: 12 }}>Findings ({result.findings.length})</div>
              {result.findings.map((f, i) => {
                const v = f.verification && VERDICT[f.verification.verdict];
                return (
                  <div key={i} style={{ borderLeft: `3px solid ${SEV_COLOR[f.severity] || '#ccc'}`, paddingLeft: 12, marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: SEV_COLOR[f.severity] }}>{f.severity}</span>
                      <strong style={{ fontSize: 14.5, color: '#26261f' }}>{f.title}</strong>
                      <span style={{ fontSize: 11.5, color: '#8a897e' }}>· {f.category} · {f.documentTitle}</span>
                      {v && <span style={{ fontSize: 11, fontWeight: 700, color: v.color, border: `1px solid ${v.color}`, borderRadius: 4, padding: '1px 6px' }}>{v.label}</span>}
                    </div>
                    <div style={{ fontSize: 13.5, color: '#3a3a32', marginTop: 5, lineHeight: 1.55 }}>{f.summary}</div>
                    <div style={{ fontSize: 13, color: '#5a5950', marginTop: 5 }}><strong>→</strong> {f.recommendation}</div>
                    {f.verification?.citations?.length > 0 && (
                      <div style={{ fontSize: 12, color: '#6b6a5f', marginTop: 5, fontStyle: 'italic' }}>“{f.verification.citations[0].cited_text}”</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {result.checklist?.length > 0 && (
            <div style={card}>
              <div style={{ ...label, marginBottom: 12 }}>Closing checklist</div>
              {result.checklist.map((c, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 9, fontSize: 13.5 }}>
                  <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: STATUS_COLOR[c.status], minWidth: 64 }}>{c.status}</span>
                  <span style={{ color: '#26261f' }}><strong>{c.item}</strong>{c.note ? ` — ${c.note}` : ''}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- "Draft a clause" tab -------------------------------------------------
function DraftTab() {
  const [instruction, setInstruction] = useState('');
  const [existingClause, setExistingClause] = useState('');
  const [precedent, setPrecedent] = useState('');
  const [governingLaw, setGoverningLaw] = useState('');
  const [party, setParty] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const run = async () => {
    setError(''); setResult(null); setCopied(false); setLoading(true);
    try {
      const r = await draftClause({ instruction, existingClause, precedent, governingLaw, party });
      setResult(r);
      logActivity({ kind: 'draft', title: r.title || 'Clause draft', summary: instruction.slice(0, 120) });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    if (result?.draft) { navigator.clipboard.writeText(result.draft); setCopied(true); setTimeout(() => setCopied(false), 1500); }
  };

  return (
    <div>
      <div style={card}>
        <label style={label}>What should the clause do?</label>
        <textarea style={{ ...input, minHeight: 70, fontFamily: 'inherit' }} placeholder='e.g. "Draft a mutual limitation-of-liability clause capping each party at 12 months of fees, carving out confidentiality and IP breaches."' value={instruction} onChange={(e) => setInstruction(e.target.value)} />
        <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={label}>Governing law (optional)</label>
            <input style={input} placeholder="e.g. Delaware" value={governingLaw} onChange={(e) => setGoverningLaw(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={label}>Favor party (optional)</label>
            <input style={input} placeholder="e.g. the buyer" value={party} onChange={(e) => setParty(e.target.value)} />
          </div>
        </div>
        <label style={{ ...label, marginTop: 12 }}>Existing clause to redline (optional)</label>
        <textarea style={{ ...input, minHeight: 60, fontFamily: 'inherit' }} placeholder="Paste a clause here to revise it instead of drafting fresh." value={existingClause} onChange={(e) => setExistingClause(e.target.value)} />
        <label style={{ ...label, marginTop: 12 }}>Firm precedent to anchor style (optional)</label>
        <textarea style={{ ...input, minHeight: 60, fontFamily: 'inherit' }} placeholder="Paste a precedent clause; the draft will mirror its style." value={precedent} onChange={(e) => setPrecedent(e.target.value)} />
        <div style={{ marginTop: 14 }}>
          <button style={btn(loading || !instruction)} disabled={loading || !instruction} onClick={run}>
            {loading ? 'Drafting…' : existingClause.trim() ? 'Redline clause' : 'Draft clause'}
          </button>
        </div>
        {error && <div style={{ color: '#b3261e', fontSize: 13, marginTop: 10 }}>{error}</div>}
      </div>

      {result && (
        <div>
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <strong style={{ fontSize: 15, color: '#26261f' }}>{result.title}</strong>
              <button onClick={copy} style={{ background: 'none', border: BORDER, borderRadius: 6, padding: '4px 10px', fontSize: 12.5, color: '#1d1d1b', cursor: 'pointer' }}>{copied ? 'Copied ✓' : 'Copy'}</button>
            </div>
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'Georgia, serif', fontSize: 14, lineHeight: 1.6, color: '#26261f', margin: 0 }}>{result.draft}</pre>
          </div>
          {result.rationale && <div style={{ ...card, fontSize: 13.5, color: '#3a3a32', lineHeight: 1.6 }}><div style={{ ...label, marginBottom: 6 }}>Rationale</div>{result.rationale}</div>}
          {result.redlineNotes?.length > 0 && (
            <div style={card}>
              <div style={{ ...label, marginBottom: 10 }}>Changes</div>
              {result.redlineNotes.map((n, i) => (
                <div key={i} style={{ fontSize: 13, color: '#3a3a32', marginBottom: 8 }}><strong>{n.change}</strong> — <span style={{ color: '#6b6a5f' }}>{n.reason}</span></div>
              ))}
            </div>
          )}
          {result.openIssues?.length > 0 && (
            <div style={card}>
              <div style={{ ...label, marginBottom: 10 }}>Open issues for the lawyer</div>
              {result.openIssues.map((o, i) => (
                <div key={i} style={{ fontSize: 13, color: '#3a3a32', marginBottom: 6 }}>• {o}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- Result renderers shared by the Assistant tab -------------------------
function AskResult({ result }) {
  const footnotes = [];
  result?.segments?.forEach((seg) => (seg.citations || []).forEach((c) => footnotes.push(c)));
  return (
    <div style={card}>
      <div style={{ fontSize: 15, lineHeight: 1.7, color: '#26261f' }}>
        {result.segments.map((seg, i) => {
          const cited = (seg.citations || []).length > 0;
          return (
            <span key={i} style={cited ? { background: '#fbf1d9', borderBottom: '1px solid #e6c86a' } : {}}>
              {seg.text}
              {cited && <sup style={{ color: '#1d1d1b', fontWeight: 700, fontSize: 11 }}>[{citationLabel(seg.citations[0])}]</sup>}
            </span>
          );
        })}
      </div>
      {footnotes.length > 0 && (
        <div style={{ marginTop: 16, borderTop: BORDER, paddingTop: 12 }}>
          <div style={{ ...label, marginBottom: 8 }}>Sources</div>
          {footnotes.map((c, i) => (
            <div key={i} style={{ fontSize: 12.5, color: '#5a5950', marginBottom: 6 }}>
              <strong style={{ color: '#1d1d1b' }}>{citationLabel(c)}</strong> “{c.cited_text}”
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CompareResult({ result }) {
  return (
    <CompareGrid result={result} />
  );
}

// Tag-cell palette (stable color per distinct value, like August's category chips).
const TAG_COLORS = ['#5b8def', '#3f9d6b', '#c07ad6', '#d6a23f', '#d6705b', '#3fa9b0', '#9b8bd6'];
function tagColor(value) {
  let h = 0;
  for (let i = 0; i < String(value).length; i++) h = (h * 31 + String(value).charCodeAt(i)) >>> 0;
  return TAG_COLORS[h % TAG_COLORS.length];
}

function cellsToCSV(result) {
  const cols = result.columns || [];
  const esc = (s) => `"${String(s == null ? '' : s).replace(/"/g, '""')}"`;
  const header = ['Document', ...cols.map((c) => c.name)].map(esc).join(',');
  const lines = (result.rows || []).map((r) => {
    const byCol = new Map((r.cells || []).map((c) => [c.column, c.value]));
    return [r.documentTitle, ...cols.map((c) => byCol.get(c.name) ?? '')].map(esc).join(',');
  });
  return [header, ...lines].join('\n');
}

// August-style sourced grid: rows = documents, columns = fields, every cell hover-reveals
// its verbatim source quote; tag columns render as colored chips. CSV export.
function confColor(c) { return c >= 85 ? '#3f7d4f' : c >= 60 ? '#b07d2b' : c > 0 ? '#b3261e' : '#a3a298'; }

function CompareGrid({ result }) {
  const cols = result.columns || [];
  const [detail, setDetail] = useState(null);   // { ri, ci, cell, col, docTitle }
  const [filter, setFilter] = useState('');
  const [verify, setVerify] = useState({});      // "ri:ci" → 'verified'|'review'|'incorrect'
  const [comments, setComments] = useState({});  // "ri:ci" → text
  const [wrap, setWrap] = useState(false);
  const today = (() => { try { return new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return ''; } })();

  const exportCSV = () => {
    const blob = new Blob([cellsToCSV(result)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'tabular-review.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const rows = (result.rows || []).filter((r) => {
    if (!filter.trim()) return true;
    const hay = [r.documentTitle, ...(r.cells || []).map((c) => `${c.value} ${c.source}`)].join(' ').toLowerCase();
    return hay.includes(filter.toLowerCase());
  });
  const tb = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 11px', borderRadius: 8, border: BORDER, background: '#fff', cursor: 'pointer', fontFamily: FONT, fontSize: 12.5, color: '#1d1d1b' };
  const tbGhost = { ...tb, border: 'none', color: '#6f6e64' };

  return (
    <div>
      {/* Run status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, fontSize: 12.5, color: '#6f6e64' }}>
        <span>Run on {today}</span><span>·</span><span style={{ color: '#3f7d4f', fontWeight: 600 }}>● Completed</span>
        <span>·</span><span>{result.rows?.length || 0} docs · {cols.length} cols · every cell sourced</span>
      </div>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button style={tb} title="Add a column in the Tabular Review tab, then re-run">▦ Add Column</button>
        <button style={tb} title="Add documents in the Tabular Review tab, then re-run">＋ Add Documents</button>
        <span style={{ ...tb, padding: '4px 8px' }}>🔎 <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter" style={{ border: 'none', outline: 'none', fontSize: 12.5, width: 90, background: 'transparent', fontFamily: FONT }} /></span>
        <button onClick={() => setWrap((w) => !w)} style={{ ...tb, fontWeight: wrap ? 700 : 500 }} title="Toggle text wrapping">↵ Text Wrapping</button>
        <div style={{ flex: 1 }} />
        <button style={tbGhost} title="Batch Mode (coming soon)">▱ Batch Mode</button>
        <button style={tbGhost} title="Save as a reusable template (use the templates in the Tabular Review tab)">🖫 Save as Template</button>
        <button onClick={exportCSV} style={tb}>⤓ Export</button>
      </div>

      <div style={{ overflowX: 'auto', border: BORDER, borderRadius: 10 }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13.5 }}>
          <thead>
            <tr style={{ background: '#faf9f4' }}>
              <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: BORDER, color: '#6f6e64', width: 36 }}>#</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: BORDER, color: '#3a3a32', position: 'sticky', left: 0, background: '#faf9f4' }}>📄 File</th>
              {cols.map((c, j) => (
                <th key={j} style={{ textAlign: 'left', padding: '10px 12px', borderBottom: BORDER, color: '#3a3a32', whiteSpace: 'nowrap' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: '#a3a298' }}>{c.type === 'tag' ? '◉' : 'T'}</span>{c.name}
                    <span style={{ color: '#cfcdc2', marginLeft: 4 }}>⋮</span>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => {
              const byCol = new Map((row.cells || []).map((c) => [c.column, c]));
              return (
                <tr key={ri}>
                  <td style={{ padding: '10px 12px', borderTop: BORDER, color: '#a3a298', verticalAlign: 'top' }}>{ri + 1}</td>
                  <td style={{ padding: '10px 12px', borderTop: BORDER, fontWeight: 600, color: '#26261f', verticalAlign: 'top', position: 'sticky', left: 0, background: '#fff', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📄 {row.documentTitle}</td>
                  {cols.map((c, ci) => {
                    const cell = byCol.get(c.name) || { value: '', source: '', confidence: 0 };
                    const absent = !cell.value || cell.value === 'Not present';
                    const key = `${ri}:${ci}`;
                    const v = verify[key];
                    return (
                      <td key={ci} onClick={() => setDetail({ ri, ci, cell, col: c, docTitle: row.documentTitle, key })}
                        style={{ padding: '10px 12px', borderTop: BORDER, color: absent ? '#a3a298' : '#26261f', verticalAlign: 'top', minWidth: 160, maxWidth: 280, cursor: 'pointer', whiteSpace: wrap ? 'normal' : 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%' }}>
                          {c.type === 'tag' && !absent ? (
                            <span style={{ fontSize: 11.5, fontWeight: 700, color: tagColor(cell.value), background: `${tagColor(cell.value)}1a`, borderRadius: 5, padding: '2px 8px' }}>{cell.value}</span>
                          ) : (
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{cell.value || '—'}</span>
                          )}
                          {!absent && cell.confidence > 0 && <span title={`Confidence ${cell.confidence}%`} style={{ width: 7, height: 7, borderRadius: '50%', background: confColor(cell.confidence), flexShrink: 0 }} />}
                          {v === 'verified' && <span style={{ color: '#3f7d4f', fontSize: 11 }}>✓</span>}
                          {v === 'incorrect' && <span style={{ color: '#b3261e', fontSize: 11 }}>✗</span>}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            <tr>
              <td style={{ padding: '10px 12px', borderTop: BORDER, color: '#a3a298' }}>{rows.length + 1}</td>
              <td colSpan={cols.length + 1} style={{ padding: '10px 12px', borderTop: BORDER, color: '#a3a298' }}>📄 Add Documents</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Cell detail panel (August's per-cell Confidence / Verify / Comments) */}
      {detail && (
        <div onClick={() => setDetail(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', display: 'flex', justifyContent: 'flex-end', zIndex: 60 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 420, maxWidth: '92vw', background: '#fff', height: '100%', overflow: 'auto', padding: 22, boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#1d1d1b' }}>{detail.col.name}</div>
              <button onClick={() => setDetail(null)} style={{ background: 'none', border: 'none', color: '#6f6e64', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>
            <div style={{ fontSize: 12.5, color: '#8a897e', marginBottom: 14 }}>📄 {detail.docTitle}</div>

            {detail.cell.confidence > 0 && (
              <span style={{ display: 'inline-block', fontSize: 12, fontWeight: 700, color: confColor(detail.cell.confidence), background: `${confColor(detail.cell.confidence)}1a`, borderRadius: 6, padding: '3px 9px', marginBottom: 14 }}>Confidence: {detail.cell.confidence}%</span>

            )}

            <div style={{ fontSize: 11.5, fontWeight: 700, color: '#8a897e', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Answer</div>
            <div style={{ fontSize: 14.5, color: '#26261f', lineHeight: 1.6, marginBottom: 16 }}>{detail.cell.value || 'Not present'}</div>

            {detail.cell.source && (
              <>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: '#8a897e', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Source</div>
                <div style={{ fontSize: 13, color: '#3a3a32', fontStyle: 'italic', lineHeight: 1.6, borderLeft: '2px solid #e6c86a', paddingLeft: 10, marginBottom: 18 }}>“{detail.cell.source}”</div>
              </>
            )}

            <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
              {[['verified', '✓ Verified', '#3f7d4f'], ['review', '⚠ Needs Review', '#b07d2b'], ['incorrect', '✗ Incorrect', '#b3261e']].map(([id, lbl, color]) => {
                const on = verify[detail.key] === id;
                return <button key={id} onClick={() => setVerify((s) => ({ ...s, [detail.key]: on ? undefined : id }))} style={{ fontSize: 12, fontWeight: 600, color: on ? '#fff' : color, background: on ? color : '#fff', border: `1px solid ${color}`, borderRadius: 7, padding: '5px 9px', cursor: 'pointer', fontFamily: FONT }}>{lbl}</button>;
              })}
            </div>

            <div style={{ fontSize: 11.5, fontWeight: 700, color: '#8a897e', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Comment</div>
            <textarea value={comments[detail.key] || ''} onChange={(e) => setComments((s) => ({ ...s, [detail.key]: e.target.value }))} placeholder="Add a note for the team…" style={{ ...input, minHeight: 70, fontFamily: 'inherit' }} />
          </div>
        </div>
      )}
    </div>
  );
}

function DiligenceResult({ result }) {
  return (
    <div>
      {result.summary && <div style={{ ...card, fontSize: 14.5, lineHeight: 1.6, color: '#26261f' }}>{result.summary}</div>}
      {result.findings?.length > 0 && (
        <div style={card}>
          <div style={{ ...label, marginBottom: 12 }}>Findings ({result.findings.length})</div>
          {result.findings.map((f, i) => {
            const v = f.verification && VERDICT[f.verification.verdict];
            return (
              <div key={i} style={{ borderLeft: `3px solid ${SEV_COLOR[f.severity] || '#ccc'}`, paddingLeft: 12, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: SEV_COLOR[f.severity] }}>{f.severity}</span>
                  <strong style={{ fontSize: 14.5, color: '#26261f' }}>{f.title}</strong>
                  <span style={{ fontSize: 11.5, color: '#8a897e' }}>· {f.category} · {f.documentTitle}</span>
                  {v && <span style={{ fontSize: 11, fontWeight: 700, color: v.color, border: `1px solid ${v.color}`, borderRadius: 4, padding: '1px 6px' }}>{v.label}</span>}
                </div>
                <div style={{ fontSize: 13.5, color: '#3a3a32', marginTop: 5, lineHeight: 1.55 }}>{f.summary}</div>
                <div style={{ fontSize: 13, color: '#5a5950', marginTop: 5 }}><strong>→</strong> {f.recommendation}</div>
              </div>
            );
          })}
        </div>
      )}
      {result.checklist?.length > 0 && (
        <div style={card}>
          <div style={{ ...label, marginBottom: 12 }}>Closing checklist</div>
          {result.checklist.map((c, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 9, fontSize: 13.5 }}>
              <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: STATUS_COLOR[c.status], minWidth: 64 }}>{c.status}</span>
              <span style={{ color: '#26261f' }}><strong>{c.item}</strong>{c.note ? ` — ${c.note}` : ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DraftResult({ result }) {
  return (
    <div>
      <div style={card}>
        <strong style={{ fontSize: 15, color: '#26261f', display: 'block', marginBottom: 8 }}>{result.title}</strong>
        <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'Georgia, serif', fontSize: 14, lineHeight: 1.6, color: '#26261f', margin: 0 }}>{result.draft}</pre>
      </div>
      {result.rationale && <div style={{ ...card, fontSize: 13.5, color: '#3a3a32', lineHeight: 1.6 }}><div style={{ ...label, marginBottom: 6 }}>Rationale</div>{result.rationale}</div>}
      {result.openIssues?.length > 0 && (
        <div style={card}>
          <div style={{ ...label, marginBottom: 10 }}>Open issues for the lawyer</div>
          {result.openIssues.map((o, i) => <div key={i} style={{ fontSize: 13, color: '#3a3a32', marginBottom: 6 }}>• {o}</div>)}
        </div>
      )}
    </div>
  );
}

function KeyTermsResult({ result }) {
  return (
    <div style={card}>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13.5 }}>
        <tbody>
          {TERM_ROWS.map(([key, lbl]) => {
            const val = result[key];
            const display = Array.isArray(val) ? val.join(', ') : val;
            return (
              <tr key={key}>
                <td style={{ padding: '7px 10px', borderBottom: BORDER, fontWeight: 600, color: '#3a3a32', width: 160, verticalAlign: 'top' }}>{lbl}</td>
                <td style={{ padding: '7px 10px', borderBottom: BORDER, color: display && display !== 'Not specified' ? '#26261f' : '#a3a298' }}>{display || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {result.notableProvisions?.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ ...label, marginBottom: 8 }}>Notable provisions</div>
          {result.notableProvisions.map((p, i) => (
            <div key={i} style={{ fontSize: 13, color: '#3a3a32', marginBottom: 7 }}><strong>{p.label}</strong> — <span style={{ color: '#5a5950' }}>{p.summary}</span></div>
          ))}
        </div>
      )}
    </div>
  );
}

// "Download .ics" — turns the extracted obligations into an importable contract calendar.
function ObligationsCalendarButton({ obligations }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const dl = async () => {
    setBusy(true); setMsg('');
    try {
      const ics = await obligationsICS(obligations || []);
      if (!/BEGIN:VEVENT/.test(ics)) { setMsg('No dated deadlines to add'); return; }
      const blob = new Blob([ics], { type: 'text/calendar' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'contract-calendar.ics'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
      <button onClick={dl} disabled={busy} style={exportBtn}>{busy ? 'Building…' : '📅 Download .ics'}</button>
      {msg && <span style={{ fontSize: 12, color: '#8a897e' }}>{msg}</span>}
    </span>
  );
}

function ObligationsResult({ result }) {
  return (
    <div style={{ ...card, overflowX: 'auto' }}>
      {(result.obligations || []).length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}><ObligationsCalendarButton obligations={result.obligations} /></div>
      )}
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
        <thead>
          <tr>{['Party', 'Obligation', 'Trigger', 'Deadline', 'Consequence'].map((h) => (
            <th key={h} style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #d9d7cc', color: '#3a3a32' }}>{h}</th>
          ))}</tr>
        </thead>
        <tbody>
          {(result.obligations || []).map((o, i) => (
            <tr key={i}>
              <td style={{ padding: '8px 10px', borderBottom: BORDER, color: '#26261f', verticalAlign: 'top', fontWeight: 600 }}>{o.party}</td>
              <td style={{ padding: '8px 10px', borderBottom: BORDER, color: '#26261f', verticalAlign: 'top' }}>{o.obligation}</td>
              <td style={{ padding: '8px 10px', borderBottom: BORDER, color: '#5a5950', verticalAlign: 'top' }}>{o.trigger}</td>
              <td style={{ padding: '8px 10px', borderBottom: BORDER, color: '#1d1d1b', verticalAlign: 'top', fontWeight: 600 }}>{o.deadline}</td>
              <td style={{ padding: '8px 10px', borderBottom: BORDER, color: '#5a5950', verticalAlign: 'top' }}>{o.consequence}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MemoResult({ result }) {
  const title = result?.title || 'Memo';
  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 10 }}>
        <button onClick={() => result?.memo && navigator.clipboard.writeText(result.memo)} style={exportBtn}>Copy</button>
        <button onClick={() => downloadWord(title, result.memo)} style={exportBtn}>⤓ Word</button>
        <button onClick={() => printPdf(title, result.memo)} style={exportBtn}>⤓ PDF</button>
      </div>
      <MemoDocument md={result.memo} />
    </div>
  );
}

// Dispatch a specialist agent's result to the right renderer (used by Agent-mode steps
// and the single-shot Default route).
function StepResult({ agent, result, error }) {
  if (error) return <div style={{ ...card, color: '#b3261e', fontSize: 13 }}>This step failed: {error}</div>;
  if (!result) return null;
  switch (agent) {
    case 'ask': return <AskResult result={result} />;
    case 'keyterms': return <KeyTermsResult result={result} />;
    case 'obligations': return <ObligationsResult result={result} />;
    case 'compare': return <CompareResult result={result} />;
    case 'diligence': return <DiligenceResult result={result} />;
    case 'playbook': return <PlaybookResult result={result} />;
    case 'draft': return <DraftResult result={result} />;
    case 'memo': return <MemoResult result={result} />;
    default: return null;
  }
}

const INTENT_LABEL = { ask: 'Ask a contract', keyterms: 'Key terms', obligations: 'Obligations', compare: 'Tabular review', diligence: 'Due diligence', draft: 'Draft a clause', playbook: 'Playbook review', memo: 'Memo' };
// Results that open in the right-hand artifact pane (August's split view), vs. inline answers.
const ARTIFACT_AGENTS = ['compare', 'diligence', 'playbook', 'memo', 'draft'];
const ARTIFACT_KIND = { compare: 'Tabular review', diligence: 'Diligence summary', playbook: 'Playbook review', memo: 'Memo', draft: 'Clause draft' };

// Prompt Library — starter prompts that fill the Assistant composer (August's Prompt Library).
const PROMPT_LIBRARY = [
  {
    group: 'Due diligence',
    prompts: [
      'Review the uploaded documents for this acquisition. Identify key financial risks, outstanding liabilities, and red flags in the contracts. Build a sourced diligence table and draft a memo for the client.',
      'Summarize the disclosure schedule and flag anything that departs from the representations in the purchase agreement.',
      'Build a closing checklist from these deal documents and note which items are still open.',
    ],
  },
  {
    group: 'Contract review',
    prompts: [
      'Compare the termination, liability, and governing-law clauses across these agreements in a table.',
      'Review this counterparty contract against our services-buyer playbook and propose redlines.',
      'Extract the key terms (parties, term, renewal, liability cap, governing law) from this contract.',
      'List every obligation, deadline, and key date in this contract that we need to diary.',
    ],
  },
  {
    group: 'Drafting',
    prompts: [
      'Draft a mutual limitation-of-liability clause capping each party at 12 months of fees, carving out confidentiality and IP breaches.',
      'Redline this indemnification clause to favor the customer and explain each change.',
      'Draft a New York–law mutual confidentiality clause for a vendor evaluation.',
    ],
  },
];

// --- "Assistant" tab (orchestrator entry) ---------------------------------
function AssistantTab({ prefill }) {
  const [request, setRequest] = useState('');
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [routed, setRouted] = useState(null);       // Default mode single-shot result
  const [mode, setMode] = useState('Agent');
  const [plan, setPlan] = useState(null);            // Agent mode: { reasoning, steps:[{agent,label,status,preview}] }
  const [agentResults, setAgentResults] = useState([]); // Agent mode: [{agent,label,result|error}]
  const [folders, setFolders] = useState([]);
  const [folderId, setFolderId] = useState('');      // optional folder context
  const [workflowId, setWorkflowId] = useState('');  // set when launched from a saved workflow
  const [savedMsg, setSavedMsg] = useState('');
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [sourcesTab, setSourcesTab] = useState('august'); // 'august' | 'drive'
  const [sourceSearch, setSourceSearch] = useState('');
  const [thinkingOpen, setThinkingOpen] = useState(true);
  const [collapsedSteps, setCollapsedSteps] = useState({});
  const [artifact, setArtifact] = useState(null); // {agent,label,result} open in the right pane
  const [promptLibOpen, setPromptLibOpen] = useState(false);
  const [thinkSecs, setThinkSecs] = useState(0);
  const thinkStart = React.useRef(0);

  React.useEffect(() => { listFolders().then((d) => setFolders(d.folders || [])).catch(() => {}); }, []);

  // Launched from a saved workflow → prefill + auto-run in Agent mode.
  const lastNonce = React.useRef(null);
  React.useEffect(() => {
    if (prefill && prefill.nonce !== lastNonce.current) {
      lastNonce.current = prefill.nonce;
      setRequest(prefill.instruction);
      setWorkflowId(prefill.workflowId || '');
      setMode('Agent');
      setTimeout(() => runWith(prefill.instruction, prefill.workflowId), 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  const addFiles = (fileList) => {
    const added = Array.from(fileList).map((file) => ({ title: file.name, file }));
    setDocs((ds) => [...ds, ...added].slice(0, 12));
  };

  const reset = () => { setRouted(null); setPlan(null); setAgentResults([]); setArtifact(null); setError(''); };

  const buildDocs = async () => {
    const documents = [];
    for (const d of docs) {
      const entry = { title: d.title };
      entry.base64 = await fileToBase64(d.file);
      entry.mediaType = d.file.type || 'application/pdf';
      documents.push(entry);
    }
    return documents;
  };

  const run = () => runWith(request, workflowId);

  const runWith = async (reqText, wfId) => {
    if (!reqText || !String(reqText).trim()) return;
    reset(); setSavedMsg(''); setLoading(true); thinkStart.current = Date.now();
    try {
      const documents = await buildDocs();
      const payload = { request: reqText, documents, folderId: folderId || undefined, workflowId: wfId || undefined };
      if (mode === 'Default') {
        const r = await assistantRequest(payload);
        setRouted(r);
        if (r.result && ARTIFACT_AGENTS.includes(r.intent)) setArtifact({ agent: r.intent, label: ARTIFACT_KIND[r.intent] || r.intent, result: r.result });
        logActivity({ kind: r.intent || 'ask', title: reqText, summary: r.reasoning });
      } else {
        // Agent mode: stream the plan + step-by-step progress.
        await streamAgentRun(payload, (e) => {
          if (e.type === 'plan') {
            setPlan({ reasoning: e.reasoning, steps: e.steps.map((s) => ({ ...s, status: 'pending' })) });
          } else if (e.type === 'step') {
            setPlan((p) => p && { ...p, steps: p.steps.map((s, i) => (i === e.index ? { ...s, status: e.status, preview: e.preview, error: e.error } : s)) });
          } else if (e.type === 'done') {
            setAgentResults(e.steps || []);
            // Auto-open the last artifact-type result in the right pane (August's split view).
            const last = [...(e.steps || [])].reverse().find((r) => r.result && ARTIFACT_AGENTS.includes(r.agent));
            if (last) setArtifact(last);
          } else if (e.type === 'error') {
            setError(e.error);
          }
        });
        logActivity({ kind: 'agent', title: reqText, summary: 'Agent run' });
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setThinkSecs(Math.max(1, Math.round((Date.now() - thinkStart.current) / 1000)));
    }
  };

  const firstName = (auth.currentUser?.displayName || '').trim().split(/\s+/)[0];
  const hasRun = routed || plan || agentResults.length > 0;

  const saveAsWorkflow = async () => {
    // eslint-disable-next-line no-alert
    const name = window.prompt('Name this workflow:', request.slice(0, 50));
    if (!name || !name.trim()) return;
    try { await createWorkflow({ name: name.trim(), instruction: request }); setSavedMsg('Saved to Workflows ✓'); } catch (e) { setSavedMsg(e.message); }
  };
  const RunFooter = () => (
    <div style={{ textAlign: 'center', marginTop: 18 }}>
      <button onClick={() => { reset(); setRequest(''); setDocs([]); setWorkflowId(''); }} style={{ ...btn(false), background: '#fff', color: '#1d1d1b', border: BORDER, marginRight: 8 }}>+ New chat</button>
      {request.trim() && <button onClick={saveAsWorkflow} style={{ ...btn(false), background: 'none', color: '#6b6a5f', border: BORDER }}>⚡ Save as workflow</button>}
      {savedMsg && <span style={{ fontSize: 12.5, color: '#3f7d4f', marginLeft: 10 }}>{savedMsg}</span>}
    </div>
  );

  const selFolder = folders.find((f) => f.id === folderId);
  const chatTitle = request.trim() ? request.trim().split(/\s+/).slice(0, 7).join(' ') : 'New chat';
  const filteredFolders = folders.filter((f) => !sourceSearch.trim() || f.name.toLowerCase().includes(sourceSearch.toLowerCase()));

  const composer = (
    <div style={{ width: '100%', maxWidth: 760, background: '#faf9f4', border: selFolder ? '1.5px solid #6cae8e' : AUG_BORDER, borderRadius: 18, padding: 18, boxShadow: selFolder ? '0 0 0 3px rgba(108,174,142,0.18)' : '0 2px 14px rgba(0,0,0,0.04)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <textarea
          style={{ flex: 1, border: 'none', outline: 'none', resize: 'vertical', minHeight: 28, fontSize: 16, fontFamily: 'inherit', color: AUG_INK, boxSizing: 'border-box', background: 'transparent' }}
          placeholder="How can I help? Use @ to add context…"
          value={request}
          onChange={(e) => setRequest(e.target.value)}
        />
        <span title="Voice" style={{ color: AUG_MUTED, fontSize: 16, paddingTop: 4 }}>𝅒</span>
        <button onClick={run} disabled={loading || !request} title="Send" style={{ width: 34, height: 34, borderRadius: '50%', border: 'none', cursor: loading || !request ? 'default' : 'pointer', flexShrink: 0, background: loading || !request ? '#d6d4ca' : AUG_INK, color: '#fff', fontSize: 15, lineHeight: 1 }}>{loading ? '…' : '→'}</button>
      </div>

      {(docs.length > 0 || selFolder) && (
        <div style={{ marginTop: 10 }}>
          {selFolder && (
            <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 12.5, color: '#1f5d3c', background: '#eef7f1', border: '1px solid #bfe0cd', borderRadius: 8, padding: '4px 9px', marginRight: 6, marginBottom: 6 }}>
              🗁 {selFolder.name} <button onClick={() => setFolderId('')} style={{ background: 'none', border: 'none', color: '#5a9a78', cursor: 'pointer', padding: '0 0 0 7px' }}>×</button>
            </span>
          )}
          {docs.map((d, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', fontSize: 12.5, color: '#33332d', background: '#fff', border: AUG_BORDER, borderRadius: 8, padding: '4px 9px', marginRight: 6, marginBottom: 6 }}>
              📄 {d.title} <button onClick={() => setDocs((ds) => ds.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: AUG_MUTED, cursor: 'pointer', padding: '0 0 0 7px' }}>×</button>
            </span>
          ))}
        </div>
      )}

      {request.trim() && (
        <div style={{ display: 'flex', gap: 16, marginTop: 12 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: AUG_MUTED, cursor: 'default' }} title="Refine the prompt (coming soon)">✨ Improve</span>
          <button onClick={saveAsWorkflow} title="Save as workflow" style={{ background: 'none', border: 'none', color: AUG_MUTED, cursor: 'pointer', fontSize: 14, padding: 0 }}>🖫</button>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
        <button onClick={() => setSourcesOpen((o) => !o)} title="Add Sources" style={{ width: 32, height: 32, borderRadius: '50%', border: AUG_BORDER, background: '#fff', cursor: 'pointer', fontSize: 17, color: AUG_INK, lineHeight: 1 }}>＋</button>
        <button onClick={() => setMode((m) => (m === 'Agent' ? 'Default' : 'Agent'))} title="Agent runs the full multi-step task; Default answers in one shot." style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: AUG_INK, border: AUG_BORDER, borderRadius: 20, padding: '6px 12px', background: '#fff', cursor: 'pointer', fontFamily: FONT }}>
          {mode === 'Agent' ? '⚡ Agent' : '◆ Default'} <span style={{ color: AUG_MUTED, fontSize: 11 }}>▾</span>
        </button>
      </div>
    </div>
  );

  // One collapsible "thinking" row.
  const stepRow = (key, icon, color, label, agentTag, detail) => {
    const open = !collapsedSteps[key];
    return (
      <div key={key} style={{ marginBottom: 6 }}>
        <button onClick={() => setCollapsedSteps((c) => ({ ...c, [key]: !c[key] }))} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0', fontFamily: FONT }}>
          <span style={{ color: AUG_MUTED, fontSize: 11 }}>{open ? '▲' : '▼'}</span>
          <span style={{ color, fontWeight: 700, fontSize: 14, width: 16, textAlign: 'center' }}>{icon}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#f1efe8', borderRadius: 7, padding: '3px 10px', fontSize: 13.5, fontWeight: 600, color: AUG_INK }}>{label}</span>
          {agentTag && <span style={{ fontSize: 11, color: '#a3a298', textTransform: 'uppercase' }}>{agentTag}</span>}
        </button>
        {open && detail && <div style={{ fontSize: 13.5, color: '#3a3a32', lineHeight: 1.6, padding: '6px 0 4px 52px' }}>{detail}</div>}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Add Sources panel */}
      {sourcesOpen && (
        <aside style={{ width: 380, flexShrink: 0, borderRight: AUG_BORDER, background: '#fff', padding: '18px 18px', boxSizing: 'border-box' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <span style={{ fontSize: 17, fontWeight: 700, color: AUG_INK }}>Select Files &amp; Folders</span>
            <button onClick={() => setSourcesOpen(false)} style={{ background: 'none', border: 'none', color: AUG_MUTED, cursor: 'pointer', fontSize: 18 }}>✕</button>
          </div>
          <div style={{ display: 'flex', gap: 18, borderBottom: AUG_BORDER, marginBottom: 14 }}>
            {[['august', '⚖ RAGRecruit Legal'], ['drive', '☁ Nextcloud']].map(([id, lbl]) => (
              <button key={id} onClick={() => setSourcesTab(id)} style={{ background: 'none', border: 'none', borderBottom: sourcesTab === id ? `2px solid ${AUG_INK}` : '2px solid transparent', padding: '6px 2px 10px', cursor: 'pointer', fontFamily: FONT, fontSize: 13.5, fontWeight: sourcesTab === id ? 700 : 500, color: sourcesTab === id ? AUG_INK : AUG_MUTED }}>{lbl}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <input style={{ ...input, background: '#fff', border: AUG_BORDER }} placeholder="Search files and folders…" value={sourceSearch} onChange={(e) => setSourceSearch(e.target.value)} />
            <button onClick={async () => { const n = window.prompt('New folder name:'); if (n && n.trim()) { try { await createFolder({ name: n.trim() }); listFolders().then((d) => setFolders(d.folders || [])); } catch (e) { setError(e.message); } } }} title="New folder" style={{ width: 38, flexShrink: 0, border: AUG_BORDER, borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 15 }}>🗀</button>
          </div>
          {sourcesTab === 'august' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 10px', borderRadius: 8, border: `1px dashed ${'#d9d7cc'}`, cursor: 'pointer', fontSize: 13.5, color: AUG_INK, marginBottom: 10 }}>
              📎 Upload from computer
              <input type="file" accept="application/pdf" multiple onChange={(e) => { addFiles(e.target.files); setSourcesOpen(false); }} style={{ display: 'none' }} />
            </label>
          )}
          {sourcesTab === 'drive' ? (
            <div style={{ fontSize: 13, color: AUG_MUTED, padding: '16px 4px', lineHeight: 1.6 }}>☁ Connect your open-source <b>Nextcloud</b> store and import files in the <b>Folders</b> tab (per folder), then select that folder here as context. Credentials are stored encrypted; use a revocable app-password.</div>
          ) : filteredFolders.length === 0 ? (
            <div style={{ fontSize: 13, color: AUG_MUTED, padding: '20px 4px' }}>No folders yet — upload files above, create a folder with 🗀, or use the Folders tab.</div>
          ) : (
            filteredFolders.map((f) => (
              <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 10px', borderRadius: 8, background: folderId === f.id ? '#f4f3ec' : 'transparent', cursor: 'pointer' }} onClick={() => setFolderId(folderId === f.id ? '' : f.id)}>
                <input type="checkbox" checked={folderId === f.id} readOnly />
                <span style={{ color: '#caa45a' }}>🗁</span>
                <span style={{ flex: 1, fontSize: 14, color: AUG_INK }}>{f.name}</span>
                <span style={{ color: AUG_MUTED }}>›</span>
              </div>
            ))
          )}
        </aside>
      )}

      {/* Chat column */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {/* Top bar */}
        {hasRun ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 26px', borderBottom: AUG_BORDER }}>
            <div style={{ fontSize: 15, color: AUG_INK }}>
              <span style={{ fontWeight: 700 }}>Current Matter</span> <span style={{ color: AUG_MUTED }}>/ {chatTitle}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 13.5 }}>
              <button onClick={() => { reset(); setRequest(''); setDocs([]); setWorkflowId(''); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: AUG_BORDER, borderRadius: 20, padding: '7px 14px', background: '#fff', cursor: 'pointer', fontFamily: FONT, fontWeight: 600, color: AUG_INK }}>＋ New Chat</button>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: AUG_MUTED, cursor: 'default' }}>⤢ Share Chat</span>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 26px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: AUG_INK }}>
              <span style={{ color: AUG_MUTED, fontSize: 15 }}>＋</span>
              <span style={{ width: 22, height: 22, borderRadius: '50%', background: '#e7e5db', color: AUG_INK, fontSize: 10, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>TB</span>
              <span style={{ fontWeight: 600 }}>Current Matter</span>
              <span style={{ color: AUG_MUTED, fontSize: 12 }}>✎</span>
            </div>
            <button onClick={() => setPromptLibOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5, color: AUG_INK, cursor: 'pointer', background: 'none', border: 'none', fontFamily: FONT }}><span>▥</span> Prompt Library</button>
          </div>
        )}

        {hasRun ? (
          /* ---- Conversation view ---- */
          <div style={{ flex: 1, padding: '22px 26px', width: '100%', maxWidth: 1000, margin: '0 auto', boxSizing: 'border-box' }}>
            {request.trim() && (
              <div style={{ marginBottom: 22 }}>
                <div style={{ background: '#fff', border: AUG_BORDER, borderRadius: 14, padding: '16px 20px', fontSize: 15.5, lineHeight: 1.6, color: AUG_INK }}>{request}</div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginTop: 8 }}>
                  {selFolder && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#33332d', background: '#fff', border: AUG_BORDER, borderRadius: 8, padding: '4px 10px' }}>🗁 {selFolder.name}</span>}
                  <button onClick={() => navigator.clipboard.writeText(request)} title="Copy" style={{ background: 'none', border: 'none', color: AUG_MUTED, cursor: 'pointer', fontSize: 14 }}>⧉</button>
                  <button onClick={saveAsWorkflow} title="Save as workflow" style={{ background: 'none', border: 'none', color: AUG_MUTED, cursor: 'pointer', fontSize: 14 }}>🖫</button>
                </div>
              </div>
            )}

            {(plan || loading) && (
              <div style={{ marginBottom: 18 }}>
                <button onClick={() => setThinkingOpen((o) => !o)} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: AUG_INK, color: '#fff', border: 'none', borderRadius: 20, padding: '7px 15px', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', fontFamily: FONT, marginBottom: 14 }}>
                  <RagLegalMark size={14} />{loading ? 'RAGRecruit Legal is thinking…' : `RAGRecruit Legal thought for ${thinkSecs}s`} <span style={{ fontSize: 11 }}>{thinkingOpen ? '▲' : '▼'}</span>
                </button>
                {thinkingOpen && (
                  <div style={{ paddingLeft: 4 }}>
                    {plan?.reasoning && stepRow('plan', '✓', '#3f7d4f', 'Planning document review', null, plan.reasoning)}
                    {plan?.steps.map((s, i) => stepRow(
                      `s${i}`,
                      s.status === 'done' ? '✓' : s.status === 'running' ? '⟳' : s.status === 'error' ? '✗' : '○',
                      s.status === 'done' ? '#3f7d4f' : s.status === 'running' ? '#1d1d1b' : s.status === 'error' ? '#b3261e' : '#a3a298',
                      s.label, s.agent, s.error || s.preview || (s.status === 'running' ? 'Working…' : 'Queued.'),
                    ))}
                  </div>
                )}
              </div>
            )}

            {agentResults.map((r, i) => (
              r.error ? (
                <div key={i} style={{ ...card, color: '#b3261e', fontSize: 13 }}>{r.label}: {r.error}</div>
              ) : ARTIFACT_AGENTS.includes(r.agent) ? (
                <div key={i} style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: AUG_INK, marginBottom: 6 }}>{ARTIFACT_KIND[r.agent] || r.label} created</div>
                  <button onClick={() => setArtifact(r)} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#f1efe8', border: AUG_BORDER, borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 13.5, fontWeight: 700, color: AUG_INK }}>
                    ▦ {r.label}
                  </button>
                </div>
              ) : (
                <div key={i} style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: AUG_MUTED, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.3 }}>{r.label}</div>
                  <StepResult agent={r.agent} result={r.result} error={r.error} />
                </div>
              )
            ))}

            {routed && (
              <div style={{ marginTop: 4 }}>
                <div style={{ fontSize: 12.5, color: AUG_MUTED, marginBottom: 12 }}>Routed to <strong style={{ color: AUG_INK }}>{INTENT_LABEL[routed.intent] || routed.intent}</strong> — {routed.reasoning}</div>
                {ARTIFACT_AGENTS.includes(routed.intent) ? (
                  <button onClick={() => setArtifact({ agent: routed.intent, label: ARTIFACT_KIND[routed.intent] || routed.intent, result: routed.result })} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#f1efe8', border: AUG_BORDER, borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 13.5, fontWeight: 700, color: AUG_INK }}>▦ {ARTIFACT_KIND[routed.intent] || routed.intent}</button>
                ) : (
                  <StepResult agent={routed.intent} result={routed.result} />
                )}
              </div>
            )}

            {error && <div style={{ color: '#b3261e', fontSize: 13, marginTop: 12 }}>{error}</div>}
            {!loading && <RunFooter />}
          </div>
        ) : (
          /* ---- Centered greeting + composer ---- */
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '24px 26px' }}>
            <div style={{ textAlign: 'center', marginBottom: 26 }}>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <RagLegalMark size={40} />
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: AUG_MUTED, background: '#fff', border: AUG_BORDER, borderRadius: 6, padding: '2px 7px' }}>RAGRecruit Legal · Beta</span>
              </div>
              <h2 style={{ fontSize: 30, fontWeight: 800, color: AUG_INK, margin: 0, letterSpacing: -0.6 }}>What can I help you with{firstName ? `, ${firstName}` : ''}?</h2>
            </div>
            {composer}
            {error && <div style={{ color: '#b3261e', fontSize: 13, marginTop: 12, textAlign: 'center' }}>{error}</div>}
            {!loading && <div style={{ maxWidth: 720, margin: '16px auto 0', fontSize: 13, color: AUG_MUTED, textAlign: 'center' }}>Invite a colleague and receive a referral bonus plus a personalized hourlong training session →</div>}
            {!loading && <div style={{ maxWidth: 720, margin: '14px auto 0', fontSize: 12, color: '#b7b6ac', textAlign: 'center', lineHeight: 1.6 }}>Try: “Run diligence on these deal docs and draft a memo” · “Compare the termination clauses in these NDAs”</div>}
          </div>
        )}
      </div>

      {/* Right-side artifact pane (August's split chat + artifact view) */}
      {artifact && (
        <aside style={{ flex: '0 0 54%', maxWidth: 800, minWidth: 380, borderLeft: AUG_BORDER, background: '#fff', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '18px 22px', borderBottom: AUG_BORDER }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: AUG_INK, display: 'flex', alignItems: 'center', gap: 8 }}>▦ {artifact.label}</div>
              <div style={{ fontSize: 12.5, color: AUG_MUTED, marginTop: 4 }}>{ARTIFACT_KIND[artifact.agent] || ''} generated by the {artifact.agent} agent · every cell sourced.</div>
            </div>
            <button onClick={() => setArtifact(null)} title="Close" style={{ background: 'none', border: 'none', color: AUG_MUTED, cursor: 'pointer', fontSize: 18, flexShrink: 0 }}>✕</button>
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: '18px 22px' }}>
            <StepResult agent={artifact.agent} result={artifact.result} error={artifact.error} />
          </div>
        </aside>
      )}

      {/* Prompt Library */}
      {promptLibOpen && (
        <div onClick={() => setPromptLibOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '8vh', zIndex: 70 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 640, maxWidth: '92vw', maxHeight: '78vh', overflow: 'auto', background: '#fff', border: AUG_BORDER, borderRadius: 14, padding: 22, boxShadow: '0 16px 50px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: 18, fontWeight: 700, color: AUG_INK }}>▥ Prompt Library</span>
              <button onClick={() => setPromptLibOpen(false)} style={{ background: 'none', border: 'none', color: AUG_MUTED, cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>
            <div style={{ fontSize: 13, color: AUG_MUTED, marginBottom: 16 }}>Pick a starter prompt — it fills the composer so you can tweak it before sending.</div>
            {PROMPT_LIBRARY.map((g) => (
              <div key={g.group} style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: AUG_MUTED, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>{g.group}</div>
                {g.prompts.map((p, i) => (
                  <button key={i} onClick={() => { setRequest(p); setPromptLibOpen(false); }} style={{ display: 'block', width: '100%', textAlign: 'left', background: '#faf9f4', border: AUG_BORDER, borderRadius: 9, padding: '11px 14px', marginBottom: 8, cursor: 'pointer', fontFamily: FONT, fontSize: 13.5, color: '#33332d', lineHeight: 1.5 }}>{p}</button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// --- "Key terms" tab ------------------------------------------------------
const TERM_ROWS = [
  ['parties', 'Parties'], ['effectiveDate', 'Effective date'], ['term', 'Term'], ['renewal', 'Renewal'],
  ['governingLaw', 'Governing law'], ['paymentTerms', 'Payment terms'], ['terminationRights', 'Termination'],
  ['liabilityCap', 'Liability cap'], ['confidentiality', 'Confidentiality'], ['assignment', 'Assignment'],
];

function KeyTermsTab() {
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const run = async () => {
    setError(''); setResult(null); setLoading(true);
    try {
      const payload = {};
      if (file) { payload.documentBase64 = await fileToBase64(file); payload.mediaType = file.type || 'application/pdf'; }
      else payload.documentText = text;
      setResult(await extractKeyTerms(payload));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={card}>
        <label style={label}>Contract</label>
        <input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files[0] || null)} style={{ marginBottom: 10, display: 'block', fontSize: 13 }} />
        {!file && <textarea style={{ ...input, minHeight: 120, fontFamily: 'inherit' }} placeholder="…or paste the contract text here" value={text} onChange={(e) => setText(e.target.value)} />}
        {file && <div style={{ fontSize: 13, color: '#6b6a5f' }}>📄 {file.name} — <button onClick={() => setFile(null)} style={{ background: 'none', border: 'none', color: '#1d1d1b', cursor: 'pointer', padding: 0 }}>remove</button></div>}
        <div style={{ marginTop: 14 }}>
          <button style={btn(loading || (!text && !file))} disabled={loading || (!text && !file)} onClick={run}>{loading ? 'Extracting…' : 'Extract key terms'}</button>
        </div>
        {error && <div style={{ color: '#b3261e', fontSize: 13, marginTop: 10 }}>{error}</div>}
      </div>

      {result && (
        <div style={card}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13.5 }}>
            <tbody>
              {TERM_ROWS.map(([key, lbl]) => {
                const val = result[key];
                const display = Array.isArray(val) ? val.join(', ') : val;
                return (
                  <tr key={key}>
                    <td style={{ padding: '7px 10px', borderBottom: BORDER, fontWeight: 600, color: '#3a3a32', width: 160, verticalAlign: 'top' }}>{lbl}</td>
                    <td style={{ padding: '7px 10px', borderBottom: BORDER, color: display && display !== 'Not specified' ? '#26261f' : '#a3a298' }}>{display || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {result.notableProvisions?.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ ...label, marginBottom: 8 }}>Notable provisions</div>
              {result.notableProvisions.map((p, i) => (
                <div key={i} style={{ fontSize: 13, color: '#3a3a32', marginBottom: 7 }}><strong>{p.label}</strong> — <span style={{ color: '#5a5950' }}>{p.summary}</span></div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- "Obligations" tab ----------------------------------------------------
function ObligationsTab() {
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const run = async () => {
    setError(''); setResult(null); setLoading(true);
    try {
      const payload = {};
      if (file) { payload.documentBase64 = await fileToBase64(file); payload.mediaType = file.type || 'application/pdf'; }
      else payload.documentText = text;
      setResult(await extractObligations(payload));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={card}>
        <label style={label}>Contract</label>
        <input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files[0] || null)} style={{ marginBottom: 10, display: 'block', fontSize: 13 }} />
        {!file && <textarea style={{ ...input, minHeight: 120, fontFamily: 'inherit' }} placeholder="…or paste the contract text here" value={text} onChange={(e) => setText(e.target.value)} />}
        {file && <div style={{ fontSize: 13, color: '#6b6a5f' }}>📄 {file.name} — <button onClick={() => setFile(null)} style={{ background: 'none', border: 'none', color: '#1d1d1b', cursor: 'pointer', padding: 0 }}>remove</button></div>}
        <div style={{ marginTop: 14 }}>
          <button style={btn(loading || (!text && !file))} disabled={loading || (!text && !file)} onClick={run}>{loading ? 'Extracting…' : 'Extract obligations'}</button>
        </div>
        {error && <div style={{ color: '#b3261e', fontSize: 13, marginTop: 10 }}>{error}</div>}
      </div>

      {result && (
        <div style={{ ...card, overflowX: 'auto' }}>
          {(result.obligations || []).length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}><ObligationsCalendarButton obligations={result.obligations} /></div>
          )}
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
            <thead>
              <tr>
                {['Party', 'Obligation', 'Trigger', 'Deadline', 'Consequence'].map((h) => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #d9d7cc', color: '#3a3a32' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result.obligations.map((o, i) => (
                <tr key={i}>
                  <td style={{ padding: '8px 10px', borderBottom: BORDER, color: '#26261f', verticalAlign: 'top', fontWeight: 600 }}>{o.party}</td>
                  <td style={{ padding: '8px 10px', borderBottom: BORDER, color: '#26261f', verticalAlign: 'top' }}>{o.obligation}</td>
                  <td style={{ padding: '8px 10px', borderBottom: BORDER, color: '#5a5950', verticalAlign: 'top' }}>{o.trigger}</td>
                  <td style={{ padding: '8px 10px', borderBottom: BORDER, color: '#1d1d1b', verticalAlign: 'top', fontWeight: 600 }}>{o.deadline}</td>
                  <td style={{ padding: '8px 10px', borderBottom: BORDER, color: '#5a5950', verticalAlign: 'top' }}>{o.consequence}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {result.obligations.length === 0 && <div style={{ fontSize: 13, color: '#8a897e' }}>No obligations identified.</div>}
        </div>
      )}
    </div>
  );
}

// --- "Memo" tab -----------------------------------------------------------
function MemoTab() {
  const [matter, setMatter] = useState('');
  const [audience, setAudience] = useState('');
  const [instructions, setInstructions] = useState('');
  const [source, setSource] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const run = async () => {
    setError(''); setResult(null); setCopied(false); setLoading(true);
    try {
      const r = await generateMemo({ matter, audience, instructions, source });
      setResult(r);
      logActivity({ kind: 'memo', title: r.title || matter || 'Memo', summary: audience ? `For ${audience}` : '' });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const copy = () => { if (result?.memo) { navigator.clipboard.writeText(result.memo); setCopied(true); setTimeout(() => setCopied(false), 1500); } };
  const downloadMd = () => {
    if (!result?.memo) return;
    const blob = new Blob([result.memo], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${slugify(result.title || 'memo')}.md`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div style={card}>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={label}>Matter (optional)</label>
            <input style={input} placeholder="e.g. Project Atlas acquisition" value={matter} onChange={(e) => setMatter(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={label}>Audience (optional)</label>
            <input style={input} placeholder="e.g. the client's GC" value={audience} onChange={(e) => setAudience(e.target.value)} />
          </div>
        </div>
        <label style={{ ...label, marginTop: 12 }}>Instructions (optional)</label>
        <input style={input} placeholder="e.g. Keep under 2 pages; emphasize the IP risks." value={instructions} onChange={(e) => setInstructions(e.target.value)} />
        <label style={{ ...label, marginTop: 12 }}>Source material</label>
        <textarea style={{ ...input, minHeight: 160, fontFamily: 'inherit' }} placeholder="Paste diligence findings, deal notes, or any raw material to turn into a memo." value={source} onChange={(e) => setSource(e.target.value)} />
        <div style={{ marginTop: 14 }}>
          <button style={btn(loading || !source)} disabled={loading || !source} onClick={run}>{loading ? 'Drafting memo…' : 'Generate memo'}</button>
        </div>
        {error && <div style={{ color: '#b3261e', fontSize: 13, marginTop: 10 }}>{error}</div>}
      </div>

      {result && (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 10 }}>
            <button onClick={copy} style={exportBtn}>{copied ? 'Copied ✓' : 'Copy'}</button>
            <button onClick={() => downloadWord(result.title || matter || 'Memo', result.memo)} style={exportBtn}>⤓ Word</button>
            <button onClick={() => printPdf(result.title || matter || 'Memo', result.memo)} style={exportBtn}>⤓ PDF</button>
            <button onClick={downloadMd} style={exportBtn}>⤓ .md</button>
          </div>
          <MemoDocument md={result.memo} />
        </div>
      )}
    </div>
  );
}

// --- "Playbook review" tab (August's "Configure" differentiator) ----------
const PB_STATUS = {
  acceptable: { label: 'On playbook', color: '#3f7d4f', bg: '#eef5ef' },
  needs_negotiation: { label: 'Negotiate', color: '#a07a1e', bg: '#fbf3df' },
  unacceptable: { label: 'Unacceptable', color: '#b3261e', bg: '#fbeceb' },
  missing: { label: 'Missing', color: '#8a5a2b', bg: '#f6efe6' },
};
const PB_RECO = {
  sign: { label: 'OK to sign', color: '#3f7d4f' },
  negotiate: { label: 'Negotiate', color: '#a07a1e' },
  escalate: { label: 'Escalate', color: '#b3261e' },
};

function PlaybookResult({ result }) {
  const reco = PB_RECO[result.recommendation] || { label: result.recommendation, color: '#6b6a5f' };
  const s = result.stats || {};
  return (
    <div>
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#fff', background: reco.color, borderRadius: 5, padding: '3px 10px' }}>{reco.label}</span>
          {result.playbook && <span style={{ fontSize: 12.5, color: '#8a897e' }}>{result.playbook.label || result.playbook.key || 'Custom playbook'} · as {result.playbook.party || 'party'}{result.playbook.source === 'custom' ? ' · custom' : ''}</span>}
        </div>
        <div style={{ fontSize: 14.5, lineHeight: 1.6, color: '#26261f' }}>{result.overallPosture}</div>
        {s.total > 0 && (
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 12, fontSize: 12.5, color: '#5a5950' }}>
            <span><strong style={{ color: PB_STATUS.acceptable.color }}>{s.acceptable || 0}</strong> on playbook</span>
            <span><strong style={{ color: PB_STATUS.needs_negotiation.color }}>{s.needs_negotiation || 0}</strong> to negotiate</span>
            <span><strong style={{ color: PB_STATUS.unacceptable.color }}>{s.unacceptable || 0}</strong> unacceptable</span>
            <span><strong style={{ color: PB_STATUS.missing.color }}>{s.missing || 0}</strong> missing</span>
            {s.dealbreakersUnmet > 0 && <span style={{ color: '#b3261e', fontWeight: 700 }}>⚠ {s.dealbreakersUnmet} dealbreaker{s.dealbreakersUnmet > 1 ? 's' : ''} unmet</span>}
          </div>
        )}
      </div>

      {result.items?.map((it, i) => {
        const st = PB_STATUS[it.status] || { label: it.status, color: '#6b6a5f', bg: '#f0eee4' };
        return (
          <div key={i} style={{ ...card, borderLeft: `3px solid ${st.color}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
              <strong style={{ fontSize: 14.5, color: '#26261f' }}>{it.clause}</strong>
              <span style={{ fontSize: 11, fontWeight: 700, color: st.color, background: st.bg, borderRadius: 4, padding: '1px 7px' }}>{st.label}</span>
              {it.isDealbreaker && <span style={{ fontSize: 10.5, fontWeight: 700, color: '#b3261e', border: '1px solid #b3261e', borderRadius: 4, padding: '0 6px' }}>DEALBREAKER</span>}
              <span style={{ fontSize: 11, color: '#a3a298', textTransform: 'uppercase' }}>{it.priority}</span>
            </div>
            <div style={{ fontSize: 13, color: '#3a3a32', marginBottom: it.gap ? 5 : 0 }}><span style={{ color: '#8a897e' }}>Their paper:</span> {it.contractPosition}</div>
            {it.gap && <div style={{ fontSize: 13, color: '#3a3a32', marginBottom: 6 }}><span style={{ color: '#8a897e' }}>Gap:</span> {it.gap}</div>}
            {it.suggestedRedline && (
              <div style={{ fontSize: 13, color: '#26261f', background: '#f7f6f0', border: BORDER, borderRadius: 6, padding: '8px 10px', fontFamily: 'Georgia, serif', lineHeight: 1.55 }}>
                <span style={{ fontFamily: FONT, fontSize: 11.5, fontWeight: 700, color: '#1d1d1b', display: 'block', marginBottom: 4 }}>SUGGESTED REDLINE</span>
                {it.suggestedRedline}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function PlaybookTab() {
  const [builtins, setBuiltins] = useState([]);
  const [saved, setSaved] = useState([]);
  const [choice, setChoice] = useState('builtin:saas-customer'); // "builtin:<key>" | "saved:<id>"
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [dealContext, setDealContext] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  React.useEffect(() => {
    listPlaybooks().then((d) => setBuiltins(d.playbooks || [])).catch(() => {});
    listMyPlaybooks().then((d) => setSaved(d.playbooks || [])).catch(() => {});
  }, []);

  const run = async () => {
    setError(''); setResult(null); setLoading(true);
    try {
      const payload = { dealContext, documentTitle: title || (file ? file.name : 'Contract') };
      if (choice.startsWith('saved:')) payload.playbookId = choice.slice(6);
      else payload.playbook = choice.slice(8); // "builtin:"
      if (file) { payload.documentBase64 = await fileToBase64(file); payload.mediaType = file.type || 'application/pdf'; }
      else payload.documentText = text;
      const r = await playbookReview(payload);
      setResult(r);
      logActivity({ kind: 'playbook', title: `Playbook review · ${r.playbook?.label || r.playbook?.key || 'custom'}`, summary: r.recommendation });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const selectedBuiltin = choice.startsWith('builtin:') && builtins.find((p) => p.key === choice.slice(8));
  const selectedSaved = choice.startsWith('saved:') && saved.find((p) => p.id === choice.slice(6));
  const selected = selectedBuiltin || selectedSaved;

  return (
    <div>
      <div style={card}>
        <label style={label}>Firm playbook</label>
        <select style={{ ...input, cursor: 'pointer' }} value={choice} onChange={(e) => setChoice(e.target.value)}>
          <optgroup label="Built-in starter playbooks">
            {builtins.length === 0 && <option value="builtin:saas-customer">SaaS subscription — customer side</option>}
            {builtins.map((p) => <option key={p.key} value={`builtin:${p.key}`}>{p.label}</option>)}
          </optgroup>
          {saved.length > 0 && (
            <optgroup label="My playbooks">
              {saved.map((p) => <option key={p.id} value={`saved:${p.id}`}>{p.name}</option>)}
            </optgroup>
          )}
        </select>
        {selected && <div style={{ fontSize: 12.5, color: '#8a897e', marginTop: 5 }}>Reviewing as <strong>{selected.party || 'party'}</strong> · {selected.ruleCount} clause positions. The counterparty's paper is scored against your preferred positions and fallbacks.</div>}
      </div>

      <div style={card}>
        <label style={label}>Counterparty contract (their paper)</label>
        <input style={{ ...input, marginBottom: 10 }} placeholder="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} />
        <input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files[0] || null)} style={{ marginBottom: 10, display: 'block', fontSize: 13 }} />
        {!file && <textarea style={{ ...input, minHeight: 120, fontFamily: 'inherit' }} placeholder="…or paste the contract text here" value={text} onChange={(e) => setText(e.target.value)} />}
        {file && <div style={{ fontSize: 13, color: '#6b6a5f' }}>📄 {file.name} — <button onClick={() => setFile(null)} style={{ background: 'none', border: 'none', color: '#1d1d1b', cursor: 'pointer', padding: 0 }}>remove</button></div>}
        <label style={{ ...label, marginTop: 14 }}>Deal context (optional)</label>
        <input style={input} placeholder="e.g. 3-year term, $250k ACV, vendor is a startup." value={dealContext} onChange={(e) => setDealContext(e.target.value)} />
        <div style={{ marginTop: 14 }}>
          <button style={btn(loading || (!text && !file))} disabled={loading || (!text && !file)} onClick={run}>{loading ? 'Reviewing against playbook…' : 'Review against playbook'}</button>
        </div>
        {error && <div style={{ color: '#b3261e', fontSize: 13, marginTop: 10 }}>{error}</div>}
      </div>

      {result && <PlaybookResult result={result} />}
    </div>
  );
}

// --- Saved-playbook manager (the "Deploy" half of Configure) ---------------
const EMPTY_RULE = { clause: '', preferred: '', fallback: '', dealbreaker: false, weight: 3 };

function PlaybooksManager() {
  const [list, setList] = useState([]);
  const [builtins, setBuiltins] = useState([]);
  const [editing, setEditing] = useState(null); // { id?, name, party, description, rules:[] }
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = () => listMyPlaybooks().then((d) => setList(d.playbooks || [])).catch((e) => setError(e.message));
  React.useEffect(() => {
    reload();
    listPlaybooks().then((d) => setBuiltins(d.playbooks || [])).catch(() => {});
  }, []);

  const startNew = () => setEditing({ name: '', party: '', description: '', rules: [{ ...EMPTY_RULE }] });
  const startEdit = (p) => setEditing({ id: p.id, name: p.name, party: p.party || '', description: p.description || '', rules: (p.rules || []).map((r) => ({ ...EMPTY_RULE, ...r })) });

  const cloneBuiltin = async (key) => {
    if (!key) return;
    const b = builtins.find((x) => x.key === key);
    setBusy(true); setError('');
    try {
      await createMyPlaybook({ name: `${b?.label || key} (copy)`, basedOn: key });
      await reload();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const setRule = (i, patch) => setEditing((ed) => ({ ...ed, rules: ed.rules.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
  const addRule = () => setEditing((ed) => ({ ...ed, rules: [...ed.rules, { ...EMPTY_RULE }] }));
  const removeRule = (i) => setEditing((ed) => ({ ...ed, rules: ed.rules.filter((_, j) => j !== i) }));

  const save = async () => {
    setBusy(true); setError('');
    try {
      const rules = editing.rules.filter((r) => r.clause.trim());
      if (!editing.name.trim()) throw new Error('Name your playbook');
      if (!rules.length) throw new Error('Add at least one clause rule');
      const body = { name: editing.name, party: editing.party, description: editing.description, rules };
      if (editing.id) await updateMyPlaybook(editing.id, body);
      else await createMyPlaybook(body);
      setEditing(null);
      await reload();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const remove = async (p) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete playbook "${p.name}"?`)) return;
    setBusy(true); setError('');
    try { await deleteMyPlaybook(p.id); await reload(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  if (editing) {
    return (
      <div>
        <div style={card}>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 2 }}>
              <label style={label}>Playbook name</label>
              <input style={input} placeholder="e.g. Our SaaS purchasing standard" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={label}>We negotiate as</label>
              <input style={input} placeholder="e.g. Customer" value={editing.party} onChange={(e) => setEditing({ ...editing, party: e.target.value })} />
            </div>
          </div>
          <label style={{ ...label, marginTop: 12 }}>Description (optional)</label>
          <input style={input} placeholder="When to use this playbook" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
        </div>

        {editing.rules.map((r, i) => (
          <div key={i} style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <input style={{ ...input, fontWeight: 600, maxWidth: 360 }} placeholder="Clause, e.g. Limitation of Liability" value={r.clause} onChange={(e) => setRule(i, { clause: e.target.value })} />
              {editing.rules.length > 1 && <button onClick={() => removeRule(i)} style={{ background: 'none', border: 'none', color: '#1d1d1b', cursor: 'pointer', fontSize: 13 }}>remove</button>}
            </div>
            <label style={label}>Preferred position</label>
            <textarea style={{ ...input, minHeight: 48, fontFamily: 'inherit', marginBottom: 8 }} placeholder="Our first-choice position on this clause" value={r.preferred} onChange={(e) => setRule(i, { preferred: e.target.value })} />
            <label style={label}>Acceptable fallback</label>
            <textarea style={{ ...input, minHeight: 40, fontFamily: 'inherit' }} placeholder="The compromise we can live with" value={r.fallback} onChange={(e) => setRule(i, { fallback: e.target.value })} />
            <div style={{ display: 'flex', gap: 18, alignItems: 'center', marginTop: 10 }}>
              <label style={{ fontSize: 13, color: '#3a3a32', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" checked={r.dealbreaker} onChange={(e) => setRule(i, { dealbreaker: e.target.checked })} /> Dealbreaker
              </label>
              <label style={{ fontSize: 13, color: '#3a3a32', display: 'flex', alignItems: 'center', gap: 6 }}>
                Weight
                <select value={r.weight} onChange={(e) => setRule(i, { weight: Number(e.target.value) })} style={{ ...input, width: 60, padding: '4px 6px', cursor: 'pointer' }}>
                  {[1, 2, 3, 4, 5].map((w) => <option key={w} value={w}>{w}</option>)}
                </select>
              </label>
            </div>
          </div>
        ))}

        <div style={{ marginBottom: 14 }}>
          <button onClick={addRule} style={{ ...btn(false), background: '#fff', color: '#1d1d1b', border: BORDER, marginRight: 10 }}>+ Add clause</button>
          <button style={btn(busy)} disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save playbook'}</button>
          <button onClick={() => setEditing(null)} style={{ ...btn(false), background: 'none', color: '#6b6a5f', marginLeft: 6 }}>Cancel</button>
        </div>
        {error && <div style={{ color: '#b3261e', fontSize: 13 }}>{error}</div>}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontSize: 13, color: '#6b6a5f' }}>Encode your firm's negotiation positions once, then review any contract against them.</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select defaultValue="" onChange={(e) => { cloneBuiltin(e.target.value); e.target.value = ''; }} style={{ ...input, width: 'auto', cursor: 'pointer' }} disabled={busy}>
            <option value="" disabled>Clone a built-in…</option>
            {builtins.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
          </select>
          <button style={btn(false)} onClick={startNew}>+ New playbook</button>
        </div>
      </div>
      {error && <div style={{ color: '#b3261e', fontSize: 13, marginBottom: 12 }}>{error}</div>}

      {list.length === 0 ? (
        <ComingSoon title="No saved playbooks yet" blurb="Create one from scratch, or clone a built-in starter (SaaS-customer, NDA-receiving, services-buyer) and tune it to your firm's positions." />
      ) : (
        list.map((p) => (
          <div key={p.id} style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#26261f' }}>{p.name}</div>
                <div style={{ fontSize: 12.5, color: '#8a897e', marginTop: 3 }}>
                  {p.party ? `As ${p.party} · ` : ''}{p.ruleCount} clause{p.ruleCount === 1 ? '' : 's'}{p.basedOn ? ` · from ${p.basedOn}` : ''}
                </div>
                {p.description && <div style={{ fontSize: 13, color: '#5a5950', marginTop: 5 }}>{p.description}</div>}
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button onClick={() => startEdit(p)} style={{ background: 'none', border: BORDER, borderRadius: 6, padding: '4px 12px', fontSize: 12.5, color: '#1d1d1b', cursor: 'pointer' }}>Edit</button>
                <button onClick={() => remove(p)} style={{ background: 'none', border: BORDER, borderRadius: 6, padding: '4px 12px', fontSize: 12.5, color: '#b3261e', cursor: 'pointer' }}>Delete</button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// Playbooks section = August's "Playbooks" nav: review a contract, or manage saved playbooks.
function PlaybooksSection() {
  const [view, setView] = useState('review');
  const subBtn = (id, text) => (
    <button onClick={() => setView(id)} style={{
      padding: '6px 14px', borderRadius: 8, border: BORDER, cursor: 'pointer', fontSize: 13, fontWeight: view === id ? 700 : 500,
      background: view === id ? '#1d1d1b' : '#fff', color: view === id ? '#fff' : '#54534a', fontFamily: FONT,
    }}>{text}</button>
  );
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        {subBtn('review', 'Review a contract')}
        {subBtn('manage', 'My playbooks')}
      </div>
      {view === 'review' ? <PlaybookTab /> : <PlaybooksManager />}
    </div>
  );
}

// --- Nextcloud browser (open-source, secure DMS — our SharePoint alternative) ----
function NextcloudBrowser({ folderId, onClose, onImported }) {
  const [status, setStatus] = useState(null);   // { connected, baseUrl, username }
  const [form, setForm] = useState({ baseUrl: '', username: '', appPassword: '', rootPath: '' });
  const [path, setPath] = useState('');
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  const browse = (p) => { setBusy(true); setError(''); dmsList(p).then((d) => { setItems(d.items || []); setPath(p); }).catch((e) => setError(e.message)).finally(() => setBusy(false)); };
  React.useEffect(() => {
    dmsStatus().then((s) => { setStatus(s); if (s.connected) browse(''); }).catch((e) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setBusy(true); setError('');
    try { await dmsConnect(form); const s = await dmsStatus(); setStatus(s); browse(''); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const disconnect = async () => {
    setBusy(true); setError('');
    try { await dmsDisconnect(); setStatus({ connected: false }); setItems([]); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const importFile = async (it) => {
    setBusy(true); setError(''); setDone('');
    try { await dmsImport({ path: it.path, folderId }); setDone(`Imported ${it.name} ✓`); onImported && onImported(); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const parent = path.includes('/') ? path.split('/').slice(0, -1).join('/') : '';
  const inp = { ...input, marginBottom: 8 };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '8vh', zIndex: 70 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 560, maxWidth: '92vw', maxHeight: '80vh', overflow: 'auto', background: '#fff', border: AUG_BORDER, borderRadius: 14, padding: 22, boxShadow: '0 16px 50px rgba(0,0,0,0.18)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <span style={{ fontSize: 17, fontWeight: 700, color: AUG_INK }}>☁ Nextcloud</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: AUG_MUTED, cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>
        <div style={{ fontSize: 12.5, color: AUG_MUTED, marginBottom: 14 }}>Open-source, self-hostable document store. Use a revocable <b>app password</b> (Nextcloud ▸ Settings ▸ Security), not your account password — it's stored encrypted.</div>
        {error && <div style={{ color: '#b3261e', fontSize: 13, marginBottom: 10 }}>{error}</div>}
        {done && <div style={{ color: '#3f7d4f', fontSize: 13, marginBottom: 10 }}>{done}</div>}

        {!status ? (
          <div style={{ color: AUG_MUTED, fontSize: 13 }}>Checking connection…</div>
        ) : !status.connected ? (
          <div>
            <input style={inp} placeholder="Server URL (https://cloud.example.com)" value={form.baseUrl} onChange={(e) => setForm({ ...form, baseUrl: e.target.value })} />
            <input style={inp} placeholder="Username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
            <input style={inp} type="password" placeholder="App password" value={form.appPassword} onChange={(e) => setForm({ ...form, appPassword: e.target.value })} />
            <input style={inp} placeholder="Root folder (optional, e.g. Matters)" value={form.rootPath} onChange={(e) => setForm({ ...form, rootPath: e.target.value })} />
            <button style={btn(busy)} disabled={busy} onClick={connect}>{busy ? 'Connecting…' : 'Connect'}</button>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, fontSize: 12.5, color: AUG_MUTED }}>
              <span>{status.username} @ {status.baseUrl} {path ? `· /${path}` : ''}</span>
              <button onClick={disconnect} style={{ background: 'none', border: 'none', color: '#b3261e', cursor: 'pointer', fontSize: 12.5 }}>Disconnect</button>
            </div>
            <div style={{ border: AUG_BORDER, borderRadius: 10 }}>
              {path && <button onClick={() => browse(parent)} style={{ display: 'flex', gap: 8, width: '100%', textAlign: 'left', padding: '10px 12px', border: 'none', borderBottom: AUG_BORDER, background: 'transparent', cursor: 'pointer', fontFamily: FONT, fontSize: 13.5 }}>↑ ..</button>}
              {busy && <div style={{ padding: '12px', fontSize: 13, color: AUG_MUTED }}>Loading…</div>}
              {!busy && items.length === 0 && <div style={{ padding: '12px', fontSize: 13, color: AUG_MUTED }}>Empty.</div>}
              {items.map((it, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderTop: i ? AUG_BORDER : 'none' }}>
                  <span>{it.isDir ? '🗁' : '📄'}</span>
                  {it.isDir ? (
                    <button onClick={() => browse(it.path)} style={{ flex: 1, textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT, fontSize: 13.5, color: AUG_INK }}>{it.name}</button>
                  ) : (
                    <span style={{ flex: 1, fontSize: 13.5, color: AUG_INK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.name} <span style={{ color: '#a3a298', fontSize: 12 }}>· {(it.size / 1024).toFixed(0)} KB</span></span>
                  )}
                  {!it.isDir && <button onClick={() => importFile(it)} disabled={busy} style={{ ...btn(busy), padding: '5px 12px', marginTop: 0 }}>Import</button>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// --- "Folders" tab — per-matter document store (August's workspace) --------
function FoldersTab() {
  const [folders, setFolders] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [docs, setDocs] = useState([]);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [ncOpen, setNcOpen] = useState(false);

  const reload = () => listFolders().then((d) => setFolders(d.folders || [])).catch((e) => setError(e.message));
  React.useEffect(() => { reload(); }, []);

  const openFolder = async (id) => {
    setOpenId(id); setDocs([]); setError('');
    try { const d = await listFolderDocuments(id); setDocs(d.documents || []); } catch (e) { setError(e.message); }
  };

  const removeFolder = async (f) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete folder "${f.name}" and its documents?`)) return;
    setBusy(true); setError('');
    try { await deleteFolder(f.id); if (openId === f.id) { setOpenId(null); setDocs([]); } await reload(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const upload = async (fileList) => {
    if (!openId) return;
    setBusy(true); setError('');
    try {
      for (const file of Array.from(fileList)) {
        const base64 = await fileToBase64(file);
        await addFolderDocument(openId, { title: file.name, base64, mediaType: file.type || 'application/pdf' });
      }
      await openFolder(openId); await reload();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const removeDoc = async (docId) => {
    setBusy(true); setError('');
    try { await deleteFolderDocument(openId, docId); await openFolder(openId); await reload(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  // "Create Folder" — name it, create empty. "Upload Folder" — pick a directory; we create a
  // folder from its name and upload the PDFs inside it.
  const createNamed = async () => {
    // eslint-disable-next-line no-alert
    const name = window.prompt('New folder name:');
    if (!name || !name.trim()) return;
    setBusy(true); setError('');
    try { await createFolder({ name: name.trim() }); await reload(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const uploadFolder = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => /\.pdf$/i.test(f.name));
    if (!files.length) return;
    const rel = files[0].webkitRelativePath || '';
    const folderName = (rel.split('/')[0] || 'Uploaded Folder').trim();
    setBusy(true); setError('');
    try {
      const { folder } = await createFolder({ name: folderName });
      for (const file of files) {
        const base64 = await fileToBase64(file);
        await addFolderDocument(folder.id, { title: file.name, base64, mediaType: file.type || 'application/pdf' });
      }
      await reload();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return ''; } };
  const openFolderObj = folders.find((f) => f.id === openId);
  const topBtn = { display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 14px', borderRadius: 9, border: AUG_BORDER, background: '#fff', cursor: 'pointer', fontFamily: FONT, fontSize: 13.5, fontWeight: 600, color: AUG_INK };

  // --- Open-folder document view ---
  if (openId) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: AUG_INK, margin: 0, letterSpacing: -0.4 }}>🗁 {openFolderObj?.name}</h1>
          <div style={{ display: 'flex', gap: 10 }}>
            <label style={{ ...topBtn }}>⬆ Upload Files<input type="file" accept="application/pdf" multiple onChange={(e) => upload(e.target.files)} style={{ display: 'none' }} /></label>
            <button onClick={() => setNcOpen(true)} style={topBtn}>☁ Import from Nextcloud</button>
            <button onClick={() => { setOpenId(null); setDocs([]); }} style={{ ...topBtn, background: 'transparent', border: 'none', color: AUG_MUTED }}>← All folders</button>
          </div>
        </div>
        {ncOpen && <NextcloudBrowser folderId={openId} onClose={() => setNcOpen(false)} onImported={() => openFolder(openId)} />}
        {error && <div style={{ color: '#b3261e', fontSize: 13, marginBottom: 12 }}>{error}</div>}
        {busy && <div style={{ fontSize: 12.5, color: AUG_MUTED, marginBottom: 10 }}>working…</div>}
        {docs.length === 0 ? (
          <FolderEmpty busy={busy} onUpload={upload} />
        ) : (
          <div style={{ background: '#fff', border: AUG_BORDER, borderRadius: 12 }}>
            {docs.map((d, i) => (
              <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', borderTop: i ? AUG_BORDER : 'none' }}>
                <span style={{ fontSize: 14, color: AUG_INK }}>📄 {d.title} <span style={{ color: '#a3a298', fontSize: 12 }}>· {(d.sizeBytes / 1024).toFixed(0)} KB</span></span>
                <button onClick={() => removeDoc(d.id)} style={{ background: 'none', border: 'none', color: AUG_MUTED, cursor: 'pointer', fontSize: 16 }} title="Remove">⋯</button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // --- Folders list (August layout) ---
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 26, fontWeight: 700, color: AUG_INK, margin: 0, letterSpacing: -0.4 }}>Folders</h1>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={createNamed} style={topBtn} disabled={busy}>▣ Create Folder</button>
          <label style={topBtn}>⬆ Upload Folder<input type="file" webkitdirectory="" directory="" multiple onChange={(e) => uploadFolder(e.target.files)} style={{ display: 'none' }} /></label>
          <button style={{ ...topBtn }} title="Import (coming soon)">⤓ Import ▾</button>
        </div>
      </div>

      <input style={{ ...input, background: '#fff', border: AUG_BORDER, padding: '11px 14px', marginBottom: 18 }} placeholder="Search…" value={newName} onChange={(e) => setNewName(e.target.value)} />
      {error && <div style={{ color: '#b3261e', fontSize: 13, marginBottom: 12 }}>{error}</div>}

      {/* Table header */}
      <div style={{ display: 'grid', gridTemplateColumns: '28px 1fr 130px 150px 80px', alignItems: 'center', padding: '0 16px 10px', fontSize: 13.5, fontWeight: 600, color: AUG_MUTED }}>
        <span><input type="checkbox" disabled /></span>
        <span>Name</span><span style={{ textAlign: 'right' }}># of Items</span><span style={{ textAlign: 'right' }}>Created At</span><span style={{ textAlign: 'right' }}>Actions</span>
      </div>

      {folders.filter((f) => !newName.trim() || f.name.toLowerCase().includes(newName.toLowerCase())).length === 0 ? (
        <FolderEmpty top onCreate={createNamed} onUpload={(fl) => uploadFolder(fl)} folderMode />
      ) : (
        <div style={{ background: '#fff', border: AUG_BORDER, borderRadius: 12 }}>
          {folders.filter((f) => !newName.trim() || f.name.toLowerCase().includes(newName.toLowerCase())).map((f, i) => (
            <div key={f.id} onClick={() => openFolder(f.id)} style={{ display: 'grid', gridTemplateColumns: '28px 1fr 130px 150px 80px', alignItems: 'center', padding: '14px 16px', borderTop: i ? AUG_BORDER : 'none', cursor: 'pointer', fontSize: 14, color: AUG_INK }}>
              <span onClick={(e) => e.stopPropagation()}><input type="checkbox" /></span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9 }}><span style={{ color: '#caa45a' }}>🗁</span>{f.name}</span>
              <span style={{ textAlign: 'right', color: AUG_MUTED }}>{f.documentCount}</span>
              <span style={{ textAlign: 'right', color: AUG_MUTED }}>{fmtDate(f.updatedAt)}</span>
              <span style={{ textAlign: 'right' }}><button onClick={(e) => { e.stopPropagation(); removeFolder(f); }} style={{ background: 'none', border: 'none', color: AUG_MUTED, cursor: 'pointer', fontSize: 16 }} title="Delete">⋯</button></span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// August's "Get Started with Folders" empty state.
function FolderEmpty({ onCreate, onUpload, folderMode }) {
  return (
    <div style={{ textAlign: 'center', padding: '80px 24px', color: AUG_MUTED }}>
      <div style={{ fontSize: 56, lineHeight: 1, marginBottom: 14 }}>🗁</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: AUG_INK, marginBottom: 8 }}>{folderMode ? 'Get Started with Folders' : 'Empty folder'}</div>
      <div style={{ fontSize: 14 }}>
        {folderMode ? (
          <>
            <button onClick={onCreate} style={{ background: 'none', border: 'none', color: '#3f6fb0', cursor: 'pointer', textDecoration: 'underline', fontSize: 14, padding: 0 }}>Create</button>
            {' '}or{' '}
            <label style={{ color: '#3f6fb0', cursor: 'pointer', textDecoration: 'underline' }}>Upload<input type="file" webkitdirectory="" directory="" multiple onChange={(e) => onUpload(e.target.files)} style={{ display: 'none' }} /></label>
            {' '}a Folder to get started.
          </>
        ) : (
          <label style={{ color: '#3f6fb0', cursor: 'pointer', textDecoration: 'underline' }}>Upload PDFs<input type="file" accept="application/pdf" multiple onChange={(e) => onUpload(e.target.files)} style={{ display: 'none' }} /> to this folder.</label>
        )}
      </div>
    </div>
  );
}

// --- "Workflows" tab — saved, reusable Agent tasks -------------------------
function WorkflowsTab({ onRun }) {
  const [list, setList] = useState([]);
  const [name, setName] = useState('');
  const [instruction, setInstruction] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);

  const reload = () => listWorkflows().then((d) => setList(d.workflows || [])).catch((e) => setError(e.message));
  React.useEffect(() => { reload(); }, []);

  const save = async () => {
    if (!name.trim() || !instruction.trim()) return;
    setBusy(true); setError('');
    try { await createWorkflow({ name: name.trim(), instruction: instruction.trim() }); setName(''); setInstruction(''); setCreating(false); await reload(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const remove = async (w) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete workflow "${w.name}"?`)) return;
    setBusy(true); setError('');
    try { await deleteWorkflow(w.id); await reload(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontSize: 13, color: '#6b6a5f' }}>Save a repeatable task once, then re-run it on new documents or folders.</div>
        <button style={btn(false)} onClick={() => setCreating((c) => !c)}>{creating ? 'Cancel' : '+ New workflow'}</button>
      </div>

      {creating && (
        <div style={card}>
          <label style={label}>Workflow name</label>
          <input style={{ ...input, marginBottom: 10 }} placeholder="e.g. Acquisition diligence + memo" value={name} onChange={(e) => setName(e.target.value)} />
          <label style={label}>Instruction (what the Agent should do)</label>
          <textarea style={{ ...input, minHeight: 80, fontFamily: 'inherit' }} placeholder="e.g. Review the uploaded documents for key financial risks and red flags, build a sourced diligence table, then draft a memo for the client." value={instruction} onChange={(e) => setInstruction(e.target.value)} />
          <div style={{ marginTop: 12 }}>
            <button style={btn(busy || !name.trim() || !instruction.trim())} disabled={busy || !name.trim() || !instruction.trim()} onClick={save}>{busy ? 'Saving…' : 'Save workflow'}</button>
          </div>
        </div>
      )}
      {error && <div style={{ color: '#b3261e', fontSize: 13, marginBottom: 12 }}>{error}</div>}

      {list.length === 0 && !creating ? (
        <ComingSoon title="No workflows yet" blurb="Create a workflow for a task you do repeatedly — e.g. 'diligence + memo' or 'NDA intake → playbook review'. Running it sends its instruction to the Assistant in Agent mode." />
      ) : (
        list.map((w) => (
          <div key={w.id} style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#26261f' }}>⚡ {w.name}</div>
                <div style={{ fontSize: 13, color: '#5a5950', marginTop: 4 }}>{w.instruction}</div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button onClick={() => onRun(w)} style={{ ...btn(false), padding: '5px 14px' }}>Run</button>
                <button onClick={() => remove(w)} style={{ background: 'none', border: BORDER, borderRadius: 6, padding: '5px 12px', fontSize: 12.5, color: '#b3261e', cursor: 'pointer' }}>Delete</button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// --- "Recents" tab — activity log -----------------------------------------
const ACT_ICON = { agent: '✦', compare: '▦', tabular: '▦', diligence: '🔍', draft: '✍', memo: '📝', playbook: '▤', ask: '❓', keyterms: '🏷', obligations: '📅' };
function relTime(d) {
  try {
    const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
}

function RecentsTab() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const reload = () => listActivity().then((d) => setItems(d.activity || [])).catch((e) => setError(e.message));
  React.useEffect(() => { reload(); }, []);

  const clear = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Clear all recent activity?')) return;
    try { await clearActivity(); await reload(); } catch (e) { setError(e.message); }
  };

  if (!items.length) {
    return <ComingSoon title="No recent activity" blurb="Your runs — agent tasks, tabular reviews, drafts, and memos — will show up here as you work." />;
  }
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button onClick={clear} style={{ background: 'none', border: BORDER, borderRadius: 6, padding: '5px 12px', fontSize: 12.5, color: '#6f6e64', cursor: 'pointer' }}>Clear all</button>
      </div>
      {error && <div style={{ color: '#b3261e', fontSize: 13, marginBottom: 12 }}>{error}</div>}
      <div style={{ background: '#fff', border: BORDER, borderRadius: 12 }}>
        {items.map((a, i) => (
          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderTop: i ? BORDER : 'none' }}>
            <span style={{ fontSize: 15, width: 22, textAlign: 'center' }}>{ACT_ICON[a.kind] || '•'}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, color: '#1d1d1b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title}</div>
              {a.summary && <div style={{ fontSize: 12.5, color: '#8a897e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.summary}</div>}
            </div>
            <span style={{ fontSize: 11.5, color: '#a3a298', textTransform: 'uppercase', flexShrink: 0 }}>{a.kind}</span>
            <span style={{ fontSize: 12.5, color: '#a3a298', flexShrink: 0, minWidth: 64, textAlign: 'right' }}>{relTime(a.createdAt)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// --- "Matters" tab — open matter/case registry (our SOS replacement) -------
const MATTER_STATUS = { open: ['Open', '#3f7d4f'], on_hold: ['On hold', '#b07d2b'], closed: ['Closed', '#8a897e'] };
function MattersTab() {
  const [list, setList] = useState([]);
  const [folders, setFolders] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ client: '', name: '', matterNumber: '', practiceArea: '' });
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');
  const [memo, setMemo] = useState(null); // { id, title, memo } — last drafted matter memo

  const reload = () => listMatters().then((d) => setList(d.matters || [])).catch((e) => setError(e.message));
  React.useEffect(() => { reload(); listFolders().then((d) => setFolders(d.folders || [])).catch(() => {}); }, []);

  const create = async () => {
    if (!form.client.trim() || !form.name.trim()) return;
    setError('');
    try { await createMatter(form); setForm({ client: '', name: '', matterNumber: '', practiceArea: '' }); setCreating(false); await reload(); } catch (e) { setError(e.message); }
  };
  const patch = async (m, body) => { try { await updateMatter(m.id, body); await reload(); } catch (e) { setError(e.message); } };
  const remove = async (m) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete matter "${m.name}"?`)) return;
    try { await deleteMatter(m.id); await reload(); } catch (e) { setError(e.message); }
  };
  const runDiligence = async (m) => {
    setBusyId(m.id); setError('');
    try { await runMatterDiligence(m.id); await reload(); } catch (e) { setError(e.message); } finally { setBusyId(''); }
  };
  const buildCalendar = async (m) => {
    setBusyId(m.id + ':cal'); setError('');
    try { await buildMatterCalendar(m.id); await reload(); } catch (e) { setError(e.message); } finally { setBusyId(''); }
  };
  const downloadCalendar = async (m) => {
    try {
      const ics = await matterCalendarICS(m.id);
      const blob = new Blob([ics], { type: 'text/calendar' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${m.name.replace(/[^a-z0-9]+/gi, '_').slice(0, 40)}-calendar.ics`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setError(e.message); }
  };
  const draftMemo = async (m) => {
    setBusyId(m.id + ':memo'); setError(''); setMemo(null);
    try {
      const r = await matterMemo(m.id);
      setMemo({ id: m.id, title: r.title || `${m.name} — memo`, memo: r.memo });
    } catch (e) { setError(e.message); } finally { setBusyId(''); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontSize: 13, color: '#6f6e64' }}>Track client matters, link a documents folder, and run agents on the live matter — results save back here.</div>
        <button style={btn(false)} onClick={() => setCreating((c) => !c)}>{creating ? 'Cancel' : '+ New matter'}</button>
      </div>
      {creating && (
        <div style={card}>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}><label style={label}>Client</label><input style={input} value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} placeholder="e.g. Meridian Family Holdings" /></div>
            <div style={{ flex: 1 }}><label style={label}>Matter name</label><input style={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Riverside Plumbing acquisition" /></div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
            <div style={{ flex: 1 }}><label style={label}>Matter # (optional)</label><input style={input} value={form.matterNumber} onChange={(e) => setForm({ ...form, matterNumber: e.target.value })} /></div>
            <div style={{ flex: 1 }}><label style={label}>Practice area (optional)</label><input style={input} value={form.practiceArea} onChange={(e) => setForm({ ...form, practiceArea: e.target.value })} placeholder="e.g. M&A" /></div>
          </div>
          <div style={{ marginTop: 12 }}><button style={btn(!form.client.trim() || !form.name.trim())} disabled={!form.client.trim() || !form.name.trim()} onClick={create}>Create matter</button></div>
        </div>
      )}
      {error && <div style={{ color: '#b3261e', fontSize: 13, marginBottom: 12 }}>{error}</div>}

      {list.length === 0 && !creating ? (
        <ComingSoon title="No matters yet" blurb="Create a matter for a client engagement, link its documents folder, then run diligence on the live matter — the summary and closing checklist save back onto the matter." />
      ) : list.map((m) => {
        const st = MATTER_STATUS[m.status] || ['', '#888'];
        return (
          <div key={m.id} style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#26261f' }}>{m.name} <span style={{ fontSize: 11.5, fontWeight: 700, color: st[1] }}>· {st[0]}</span></div>
                <div style={{ fontSize: 12.5, color: '#8a897e', marginTop: 3 }}>{m.client}{m.matterNumber ? ` · #${m.matterNumber}` : ''}{m.practiceArea ? ` · ${m.practiceArea}` : ''}</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <select value={m.status} onChange={(e) => patch(m, { status: e.target.value })} style={{ ...input, width: 'auto', padding: '4px 8px', cursor: 'pointer' }}>
                  {Object.entries(MATTER_STATUS).map(([k, v]) => <option key={k} value={k}>{v[0]}</option>)}
                </select>
                <button onClick={() => remove(m)} style={{ background: 'none', border: BORDER, borderRadius: 6, padding: '4px 10px', fontSize: 12.5, color: '#b3261e', cursor: 'pointer' }}>Delete</button>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12.5, color: '#6f6e64' }}>Documents folder:</span>
              <select value={m.folderId || ''} onChange={(e) => patch(m, { folderId: e.target.value })} style={{ ...input, width: 'auto', padding: '5px 8px', cursor: 'pointer' }}>
                <option value="">— none —</option>
                {folders.map((f) => <option key={f.id} value={f.id}>🗁 {f.name} ({f.documentCount})</option>)}
              </select>
              <button style={btn(busyId === m.id || !m.folderId)} disabled={busyId === m.id || !m.folderId} onClick={() => runDiligence(m)}>{busyId === m.id ? 'Running…' : 'Run diligence'}</button>
              <button style={{ ...btn(busyId === `${m.id}:cal` || !m.folderId), background: '#fff', color: '#1d1d1b', border: BORDER }} disabled={busyId === `${m.id}:cal` || !m.folderId} onClick={() => buildCalendar(m)}>{busyId === `${m.id}:cal` ? 'Extracting…' : '📅 Build calendar'}</button>
              {(m.obligations || []).length > 0 && <button style={{ ...exportBtn }} onClick={() => downloadCalendar(m)}>⤓ .ics ({m.obligations.length})</button>}
              {(m.summary || (m.obligations || []).length > 0) && (
                <button style={{ ...btn(busyId === `${m.id}:memo`), background: '#fff', color: '#1d1d1b', border: BORDER }} disabled={busyId === `${m.id}:memo`} onClick={() => draftMemo(m)}>{busyId === `${m.id}:memo` ? 'Drafting…' : '📝 Draft memo'}</button>
              )}
            </div>
            {m.summary && (
              <div style={{ marginTop: 12, borderTop: BORDER, paddingTop: 12 }}>
                <div style={{ ...label, marginBottom: 6 }}>Diligence summary {m.lastRunAt ? `· ${new Date(m.lastRunAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}</div>
                <div style={{ fontSize: 13.5, color: '#3a3a32', lineHeight: 1.6 }}>{m.summary}</div>
                {(m.checklist || []).length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    {m.checklist.map((c, i) => (
                      <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 7, fontSize: 13 }}>
                        <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: STATUS_COLOR[c.status] || '#888', minWidth: 60 }}>{c.status}</span>
                        <span style={{ color: '#26261f' }}><strong>{c.item}</strong>{c.note ? ` — ${c.note}` : ''}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {memo && memo.id === m.id && (
              <div style={{ marginTop: 12, borderTop: BORDER, paddingTop: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={label}>{memo.title}</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => downloadWord(memo.title, memo.memo)} style={exportBtn}>⤓ Word</button>
                    <button onClick={() => printPdf(memo.title, memo.memo)} style={exportBtn}>⤓ PDF</button>
                    <button onClick={() => setMemo(null)} style={{ ...exportBtn }}>✕</button>
                  </div>
                </div>
                <div style={{ fontSize: 13.5, color: '#3a3a32', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{memo.memo}</div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// --- Configure: the per-firm profile that makes every agent firm-relative -------
const RISK_OPTIONS = [
  ['conservative', 'Conservative — flag aggressively, prefer protective positions'],
  ['balanced', 'Balanced — weigh risk pragmatically, flag material issues'],
  ['aggressive', 'Aggressive — only material/deal-breaking risk'],
];
function FirmProfileTab() {
  const [form, setForm] = useState({
    firmName: '', jurisdictions: '', practiceAreas: '', defaultParty: '',
    riskTolerance: 'balanced', houseStyle: '', preferences: '',
  });
  const [preview, setPreview] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    getFirmProfile().then((d) => {
      const p = d.profile || {};
      setForm({
        firmName: p.firmName || '', jurisdictions: (p.jurisdictions || []).join(', '),
        practiceAreas: (p.practiceAreas || []).join(', '), defaultParty: p.defaultParty || '',
        riskTolerance: p.riskTolerance || 'balanced', houseStyle: p.houseStyle || '', preferences: p.preferences || '',
      });
    }).catch((e) => setError(e.message));
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    setBusy(true); setError(''); setStatus('');
    try {
      const body = {
        firmName: form.firmName,
        jurisdictions: form.jurisdictions.split(',').map((s) => s.trim()).filter(Boolean),
        practiceAreas: form.practiceAreas.split(',').map((s) => s.trim()).filter(Boolean),
        defaultParty: form.defaultParty, riskTolerance: form.riskTolerance,
        houseStyle: form.houseStyle, preferences: form.preferences,
      };
      const r = await saveFirmProfile(body);
      setPreview(r.preview || '');
      setStatus('Saved ✓ — every agent now assesses relative to this profile.');
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div style={{ fontSize: 13, color: '#6f6e64', marginBottom: 14 }}>
        Your firm's profile configures every agent — Ask, Diligence, Playbook review, Drafting, and Memos
        are assessed <b>relative to this firm</b>, not a generic ideal. Leave blank to keep the generic behavior.
      </div>
      {error && <div style={{ color: '#b3261e', fontSize: 13, marginBottom: 12 }}>{error}</div>}
      <div style={card}>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}><label style={label}>Firm name</label><input style={input} value={form.firmName} onChange={set('firmName')} placeholder="e.g. Smith LLP" /></div>
          <div style={{ flex: 1 }}><label style={label}>We usually act for</label><input style={input} value={form.defaultParty} onChange={set('defaultParty')} placeholder="e.g. the buyer / the customer" /></div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
          <div style={{ flex: 1 }}><label style={label}>Jurisdictions (comma-separated)</label><input style={input} value={form.jurisdictions} onChange={set('jurisdictions')} placeholder="e.g. Ontario, Canada; Delaware, US" /></div>
          <div style={{ flex: 1 }}><label style={label}>Practice areas (comma-separated)</label><input style={input} value={form.practiceAreas} onChange={set('practiceAreas')} placeholder="e.g. M&A, Commercial, IP" /></div>
        </div>
        <div style={{ marginTop: 12 }}>
          <label style={label}>Risk posture</label>
          <select style={{ ...input, cursor: 'pointer' }} value={form.riskTolerance} onChange={set('riskTolerance')}>
            {RISK_OPTIONS.map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
          </select>
        </div>
        <div style={{ marginTop: 12 }}><label style={label}>House style (drafting)</label><textarea style={{ ...input, minHeight: 56 }} value={form.houseStyle} onChange={set('houseStyle')} placeholder="e.g. Plain language; defined terms capitalized; British spelling." /></div>
        <div style={{ marginTop: 12 }}><label style={label}>Firm standards / non-negotiables</label><textarea style={{ ...input, minHeight: 72 }} value={form.preferences} onChange={set('preferences')} placeholder="e.g. Always require mutual liability caps; reject unilateral indemnities; data-breach carve-outs must be uncapped." /></div>
        <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
          <button style={btn(busy)} disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save firm profile'}</button>
          {status && <span style={{ fontSize: 12.5, color: '#3f7d4f' }}>{status}</span>}
        </div>
      </div>
      {preview && (
        <div style={{ ...card, marginTop: 14 }}>
          <div style={{ ...label, marginBottom: 6 }}>What the agents now see (injected into every prompt)</div>
          <div style={{ fontSize: 12.5, color: '#3a3a32', lineHeight: 1.6, whiteSpace: 'pre-wrap', fontFamily: 'ui-monospace, Menlo, monospace' }}>{preview}</div>
        </div>
      )}
    </div>
  );
}

// --- "Integration keys" — long-lived auth for the native add-ins/connectors ----
function KeysTab() {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState('');
  const [fresh, setFresh] = useState('');   // the just-created key (shown once)
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const reload = () => listApiKeys().then((d) => setKeys(d.keys || [])).catch((e) => setError(e.message));
  React.useEffect(() => { reload(); }, []);

  const create = async () => {
    setError(''); setFresh('');
    try { const r = await createApiKey(name.trim() || 'Integration key'); setFresh(r.key); setName(''); await reload(); } catch (e) { setError(e.message); }
  };
  const revoke = async (k) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Revoke "${k.name}"? Any integration using it will stop working.`)) return;
    try { await deleteApiKey(k.id); await reload(); } catch (e) { setError(e.message); }
  };

  return (
    <div>
      <div style={{ fontSize: 13, color: '#6f6e64', marginBottom: 14, lineHeight: 1.6 }}>
        Create a long-lived key to authenticate the <b>Word add-in</b>, <b>Gmail add-on</b>, and <b>Nextcloud</b> connector. Unlike a browser session token, it doesn't expire — paste it into each integration's settings. Only the key's hash is stored; revoke any time.
      </div>
      <div style={{ ...card, display: 'flex', gap: 8, alignItems: 'center' }}>
        <input style={input} placeholder="Key name (e.g. Word add-in)" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && create()} />
        <button style={btn(false)} onClick={create}>+ Create key</button>
      </div>
      {error && <div style={{ color: '#b3261e', fontSize: 13, marginBottom: 12 }}>{error}</div>}
      {fresh && (
        <div style={{ ...card, borderLeft: '3px solid #3f7d4f' }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: '#3f7d4f', marginBottom: 6 }}>Copy this key now — it won't be shown again.</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <code style={{ flex: 1, fontSize: 12.5, background: '#f4f3ec', border: BORDER, borderRadius: 6, padding: '8px 10px', wordBreak: 'break-all' }}>{fresh}</code>
            <button onClick={() => { navigator.clipboard.writeText(fresh); setCopied(true); setTimeout(() => setCopied(false), 1500); }} style={{ ...btn(false), padding: '7px 12px', marginTop: 0 }}>{copied ? 'Copied ✓' : 'Copy'}</button>
          </div>
        </div>
      )}
      {keys.length === 0 ? (
        <ComingSoon title="No keys yet" blurb="Create a key above, then paste it into the Word add-in / Gmail add-on / Nextcloud connector settings." />
      ) : (
        <div style={{ background: '#fff', border: BORDER, borderRadius: 12 }}>
          {keys.map((k, i) => (
            <div key={k.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderTop: i ? BORDER : 'none' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#1d1d1b' }}>{k.name}</div>
                <div style={{ fontSize: 12.5, color: '#8a897e' }}><code>{k.prefix}…</code> · {k.lastUsedAt ? `last used ${new Date(k.lastUsedAt).toLocaleDateString()}` : 'never used'}</div>
              </div>
              <button onClick={() => revoke(k)} style={{ background: 'none', border: BORDER, borderRadius: 6, padding: '4px 12px', fontSize: 12.5, color: '#b3261e', cursor: 'pointer' }}>Revoke</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- "Compare versions" tab — word-level redline between two versions ------
function DiffTab() {
  const [before, setBefore] = useState('');
  const [after, setAfter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const run = async () => {
    setError(''); setResult(null); setLoading(true);
    try { setResult(await diffVersions({ before, after })); } catch (e) { setError(e.message); } finally { setLoading(false); }
  };
  const segStyle = (t) => t === 'add'
    ? { background: '#eaf6ee', color: '#1f5d3c', textDecoration: 'none' }
    : t === 'del'
      ? { background: '#fbeceb', color: '#b3261e', textDecoration: 'line-through' }
      : { color: '#26261f' };

  return (
    <div>
      <p style={{ fontSize: 13, color: '#6f6e64', marginTop: 0 }}>Paste two versions of a clause or contract to see a word-level redline (additions in green, deletions struck through).</p>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 300px' }}>
          <label style={label}>Original (before)</label>
          <textarea style={{ ...input, minHeight: 160, fontFamily: 'inherit' }} value={before} onChange={(e) => setBefore(e.target.value)} placeholder="Paste the original version…" />
        </div>
        <div style={{ flex: '1 1 300px' }}>
          <label style={label}>Revised (after)</label>
          <textarea style={{ ...input, minHeight: 160, fontFamily: 'inherit' }} value={after} onChange={(e) => setAfter(e.target.value)} placeholder="Paste the revised version…" />
        </div>
      </div>
      <div style={{ marginTop: 14 }}>
        <button style={btn(loading || (!before && !after))} disabled={loading || (!before && !after)} onClick={run}>{loading ? 'Comparing…' : 'Compare versions'}</button>
      </div>
      {error && <div style={{ color: '#b3261e', fontSize: 13, marginTop: 10 }}>{error}</div>}
      {result && (
        <div style={{ ...card, marginTop: 16 }}>
          <div style={{ fontSize: 12.5, color: '#6f6e64', marginBottom: 10 }}>
            <span style={{ color: '#1f5d3c', fontWeight: 700 }}>+{result.stats?.added || 0}</span> added · <span style={{ color: '#b3261e', fontWeight: 700 }}>−{result.stats?.removed || 0}</span> removed · {result.stats?.unchanged || 0} unchanged
          </div>
          <div style={{ fontFamily: 'Georgia, serif', fontSize: 14.5, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
            {(result.diff || []).map((s, i) => <span key={i} style={segStyle(s.type)}>{s.text}</span>)}
          </div>
        </div>
      )}
    </div>
  );
}

// --- Honest placeholder for August features without a backend yet ----------
function ComingSoon({ title, blurb }) {
  return (
    <div style={{ ...card, textAlign: 'center', padding: '40px 24px', color: '#8a897e' }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: '#3a3a32', marginBottom: 8 }}>{title}</div>
      <div style={{ fontSize: 13.5, lineHeight: 1.6, maxWidth: 460, margin: '0 auto' }}>{blurb}</div>
    </div>
  );
}

// August-style left workspace nav. Sections mirror the real product
// (Assistant · Folders · Tabular Review · Workflows · Playbooks · Recents),
// with our extra single-doc tools grouped under "Tools".
// August's neutral palette (matches the product UI: cream canvas, black accents).
const AUG_BG = '#f5f4ee';
const AUG_SIDEBAR = '#f2f1ea';
const AUG_BORDER = '1px solid #e4e2d8';
const AUG_INK = '#1d1d1b';
const AUG_MUTED = '#6f6e64';

// RAGRecruit Legal emblem — a circular roundel (like the brand mark) with scales of justice
// inside, in the product ink. Reads clearly as "legal"; distinct from August's angular "A".
function RagLegalMark({ size = 22 }) {
  const cream = '#faf9f4';
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="11" fill={AUG_INK} />
      <g stroke={cream} strokeWidth="1.15" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 6.2v11" />
        <path d="M8.5 17.6h7" />
        <path d="M6.4 8.6h11.2" />
        <circle cx="12" cy="6" r="0.95" fill={cream} stroke="none" />
        <path d="M6.4 8.6 4.5 12.2h3.8z" fill={cream} fillOpacity="0.22" />
        <path d="M17.6 8.6 15.7 12.2h3.8z" fill={cream} fillOpacity="0.22" />
      </g>
    </svg>
  );
}

// Project sub-nav (the 6 items under a project) — these drive the tabs.
const PROJECT_NAV = [
  { id: 'assistant', label: 'Assistant', icon: 'A' },
  { id: 'matters', label: 'Matters', icon: '⚖' },
  { id: 'folders', label: 'Folders', icon: '🗀' },
  { id: 'compare', label: 'Tabular Review', icon: '▦' },
  { id: 'workflows', label: 'Workflows', icon: '⚡' },
  { id: 'playbook', label: 'Playbooks', icon: '▤' },
  { id: 'configure', label: 'Configure', icon: '⚙' },
  { id: 'recents', label: 'Recents', icon: '↺' },
];
// The single-doc tools live under "+ Create new" (August keeps the sidebar to the 6 items).
const CREATE_MENU = [
  { id: 'assistant', label: 'New chat' },
  { id: 'matters', label: 'Matter' },
  { id: 'compare', label: 'Tabular review' },
  { id: 'ask', label: 'Ask a contract' },
  { id: 'keyterms', label: 'Key terms' },
  { id: 'obligations', label: 'Obligations' },
  { id: 'diligence', label: 'Due diligence' },
  { id: 'draft', label: 'Draft a clause' },
  { id: 'diff', label: 'Compare versions' },
  { id: 'memo', label: 'Memo' },
  { id: 'keys', label: 'Integration key' },
];

export default function LegalPanel() {
  const [tab, setTab] = useState('assistant');
  const [prefill, setPrefill] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchData, setSearchData] = useState({ folders: [], workflows: [], playbooks: [] });
  const runWorkflow = (w) => { setPrefill({ instruction: w.instruction, workflowId: w.id, nonce: Date.now() }); setTab('assistant'); };

  const openSearch = () => {
    setSearchOpen(true);
    Promise.all([listFolders().catch(() => ({})), listWorkflows().catch(() => ({})), listMyPlaybooks().catch(() => ({}))])
      .then(([f, w, p]) => setSearchData({ folders: f.folders || [], workflows: w.workflows || [], playbooks: p.playbooks || [] }));
  };
  const searchHits = (() => {
    const q = searchQ.trim().toLowerCase();
    if (!q) return [];
    const hit = (name) => name.toLowerCase().includes(q);
    return [
      ...searchData.folders.filter((x) => hit(x.name)).map((x) => ({ icon: '🗁', label: x.name, kind: 'Folder', go: 'folders' })),
      ...searchData.workflows.filter((x) => hit(x.name)).map((x) => ({ icon: '⚡', label: x.name, kind: 'Workflow', go: 'workflows' })),
      ...searchData.playbooks.filter((x) => hit(x.name)).map((x) => ({ icon: '▤', label: x.name, kind: 'Playbook', go: 'playbook' })),
    ].slice(0, 20);
  })();

  const userName = (auth.currentUser?.displayName || auth.currentUser?.email || 'Account');
  const initials = userName.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('') || 'A';

  const projItem = (item) => {
    const active = tab === item.id;
    return (
      <button key={item.id} onClick={() => setTab(item.id)} style={{
        display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
        padding: '7px 10px', borderRadius: 8, border: 'none', cursor: 'pointer', fontFamily: FONT,
        fontSize: 13.5, fontWeight: active ? 600 : 500, marginBottom: 1,
        background: active ? '#fff' : 'transparent', color: active ? AUG_INK : '#46453e',
        boxShadow: active ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
      }}>
        <span style={{ fontSize: 13, width: 16, textAlign: 'center', color: active ? AUG_INK : AUG_MUTED }}>{item.icon}</span>
        {item.label}
      </button>
    );
  };

  const globalItem = (icon, lbl, onClick) => (
    <button onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '7px 10px', borderRadius: 8, border: 'none', background: 'transparent', cursor: onClick ? 'pointer' : 'default', fontFamily: FONT, fontSize: 13.5, color: '#46453e' }}>
      <span style={{ fontSize: 13, width: 16, textAlign: 'center', color: AUG_MUTED }}>{icon}</span>{lbl}
    </button>
  );

  return (
    <div style={{ minHeight: '100vh', background: AUG_BG, fontFamily: FONT, display: 'flex' }}>
      {/* Left sidebar (August) */}
      <aside style={{ width: 248, flexShrink: 0, background: AUG_SIDEBAR, borderRight: AUG_BORDER, padding: '14px 12px', display: 'flex', flexDirection: 'column', position: 'sticky', top: 0, height: '100vh', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 4px', marginBottom: 14 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><RagLegalMark size={20} /><span style={{ fontSize: 15, fontWeight: 800, color: AUG_INK, letterSpacing: -0.3 }}>RAGRecruit Legal</span><span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: AUG_MUTED, background: '#fff', border: AUG_BORDER, borderRadius: 5, padding: '1px 5px' }}>Beta</span></span>
          <Link to="/" title="Back to recruiting" style={{ color: AUG_MUTED, textDecoration: 'none', fontSize: 16 }}>⮌</Link>
        </div>

        {/* Create new (dropdown) */}
        <div style={{ position: 'relative', marginBottom: 8 }}>
          <button onClick={() => setCreateOpen((o) => !o)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '8px 11px', borderRadius: 9, border: AUG_BORDER, background: '#fff', cursor: 'pointer', fontFamily: FONT, fontSize: 13.5, fontWeight: 600, color: AUG_INK }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><span style={{ fontSize: 15 }}>＋</span> Create new</span>
            <span style={{ color: AUG_MUTED, fontSize: 11 }}>▾</span>
          </button>
          {createOpen && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: '#fff', border: AUG_BORDER, borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.10)', padding: 6, zIndex: 30 }}>
              {CREATE_MENU.map((m) => (
                <button key={m.id} onClick={() => { setTab(m.id); setCreateOpen(false); }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', borderRadius: 7, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: FONT, fontSize: 13, color: '#33332d' }}>{m.label}</button>
              ))}
            </div>
          )}
        </div>

        {/* Search */}
        <button onClick={openSearch} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '7px 11px', borderRadius: 9, color: AUG_MUTED, fontSize: 13.5, marginBottom: 14, cursor: 'pointer', background: 'none', border: 'none', fontFamily: FONT, textAlign: 'left' }}>
          <span style={{ fontSize: 13 }}>🔍</span> Search
        </button>

        {/* Projects */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 6px', marginBottom: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: AUG_MUTED }}>Projects</span>
          <span style={{ fontSize: 15, color: AUG_MUTED, cursor: 'pointer' }}>＋</span>
        </div>
        {globalItem('👤', 'Personal')}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 8, background: '#e7e5db', marginTop: 1 }}>
          <span style={{ fontSize: 13, color: AUG_INK }}>🗁</span>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: AUG_INK, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Current Matter</span>
          <span style={{ color: AUG_MUTED, fontSize: 14 }}>⋯</span>
        </div>
        <div style={{ paddingLeft: 8, marginTop: 2 }}>
          {PROJECT_NAV.map(projItem)}
        </div>

        {/* Agent-mode promo card */}
        <div style={{ marginTop: 14, background: 'linear-gradient(135deg,#2b3a52,#46618a)', borderRadius: 12, padding: '12px 12px 10px', color: '#fff' }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, opacity: 0.85, marginBottom: 4 }}>⚡ Agent Mode</div>
          <div style={{ fontSize: 12.5, lineHeight: 1.4 }}>Introducing Agent Mode — fully autonomous, multi-step task execution.</div>
        </div>

        {/* Global nav */}
        <div style={{ marginTop: 14 }}>
          {globalItem('⋯', 'All Projects')}
          {globalItem('🛡', 'Admin')}
          {globalItem('🎓', 'Academy')}
          {globalItem('🔔', 'Notifications', () => setTab('recents'))}
          {globalItem('⬆', 'Uploads', () => setTab('folders'))}
        </div>

        {/* User */}
        <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 9, padding: '8px 6px 0', borderTop: AUG_BORDER }}>
          <span style={{ width: 26, height: 26, borderRadius: '50%', background: '#cdd7e6', color: '#2b3a52', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{initials}</span>
          <span style={{ fontSize: 13, color: '#33332d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userName}</span>
        </div>
      </aside>

      {/* Main content */}
      <main style={{ flex: 1, minWidth: 0, overflowX: 'auto', display: 'flex', flexDirection: 'column' }} onClick={() => createOpen && setCreateOpen(false)}>
        {tab === 'assistant' ? (
          <div style={{ flex: 1, padding: '0' }}><AssistantTab prefill={prefill} /></div>
        ) : (
          <div style={{ padding: '24px 30px' }}>
            <div style={{ maxWidth: 1040, margin: '0 auto' }}>
              {tab === 'matters' && <SectionPage title="Matters"><MattersTab /></SectionPage>}
              {tab === 'folders' && <FoldersTab />}
              {tab === 'compare' && <SectionPage title="Tabular Review"><CompareTab /></SectionPage>}
              {tab === 'workflows' && <SectionPage title="Workflows"><WorkflowsTab onRun={runWorkflow} /></SectionPage>}
              {tab === 'playbook' && <SectionPage title="Playbooks"><PlaybooksSection /></SectionPage>}
              {tab === 'configure' && <SectionPage title="Configure"><FirmProfileTab /></SectionPage>}
              {tab === 'recents' && <SectionPage title="Recents"><RecentsTab /></SectionPage>}
              {tab === 'ask' && <SectionPage title="Ask a contract"><AskTab /></SectionPage>}
              {tab === 'keyterms' && <SectionPage title="Key terms"><KeyTermsTab /></SectionPage>}
              {tab === 'obligations' && <SectionPage title="Obligations"><ObligationsTab /></SectionPage>}
              {tab === 'diligence' && <SectionPage title="Due diligence"><DiligenceTab /></SectionPage>}
              {tab === 'draft' && <SectionPage title="Draft a clause"><DraftTab /></SectionPage>}
              {tab === 'diff' && <SectionPage title="Compare versions"><DiffTab /></SectionPage>}
              {tab === 'memo' && <SectionPage title="Memo"><MemoTab /></SectionPage>}
              {tab === 'keys' && <SectionPage title="Integration keys"><KeysTab /></SectionPage>}
            </div>
          </div>
        )}
      </main>

      {/* Global search */}
      {searchOpen && (
        <div onClick={() => { setSearchOpen(false); setSearchQ(''); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '12vh', zIndex: 80 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 560, maxWidth: '92vw', background: '#fff', border: AUG_BORDER, borderRadius: 14, boxShadow: '0 16px 50px rgba(0,0,0,0.18)', overflow: 'hidden' }}>
            <input autoFocus value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Search folders, workflows, playbooks…" style={{ width: '100%', border: 'none', borderBottom: AUG_BORDER, outline: 'none', padding: '16px 18px', fontSize: 15, fontFamily: FONT, boxSizing: 'border-box' }} />
            <div style={{ maxHeight: '50vh', overflow: 'auto', padding: 8 }}>
              {!searchQ.trim() ? (
                <div style={{ fontSize: 13, color: AUG_MUTED, padding: '14px 12px' }}>Type to search your workspace.</div>
              ) : searchHits.length === 0 ? (
                <div style={{ fontSize: 13, color: AUG_MUTED, padding: '14px 12px' }}>No matches.</div>
              ) : searchHits.map((h, i) => (
                <button key={i} onClick={() => { setTab(h.go); setSearchOpen(false); setSearchQ(''); }} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: 8, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: FONT }}>
                  <span>{h.icon}</span>
                  <span style={{ flex: 1, fontSize: 14, color: AUG_INK }}>{h.label}</span>
                  <span style={{ fontSize: 11.5, color: AUG_MUTED, textTransform: 'uppercase' }}>{h.kind}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SectionPage({ title, children }) {
  return (
    <div>
      <h1 style={{ fontSize: 26, fontWeight: 700, color: AUG_INK, margin: '0 0 20px', letterSpacing: -0.4 }}>{title}</h1>
      {children}
    </div>
  );
}
